<template>
  <section :class="['studio-page', { embedded }]">
    <header v-if="!embedded" class="studio-header">
      <div><small>STARLIVE STUDIO</small><h1>{{ $t("studioUi.title") }}</h1></div>
      <button type="button" :aria-label="$t('common.cancel')" data-analytics="studio.close" @click="$emit('close')"><i class="el-icon-close" /></button>
    </header>

    <main class="studio-scroll">
      <section v-if="loading && !overview" class="studio-loading"><i class="el-icon-loading" />{{ $t("common.loading") }}</section>
      <template v-else-if="overview">
        <section class="studio-hero">
          <div>
            <small>{{ $t("studioUi.workspace") }}</small>
            <h2 v-if="!embedded">{{ overview.author ? overview.author.name : $t("studioUi.notActivated") }}</h2>
            <p>{{ $t("studioBusiness.heroDescription") }}</p>
          </div>
          <span><strong>{{ $n(overview.counts.live) }}</strong>{{ $t("studioUi.totalWorks") }}</span>
        </section>

        <button
          type="button"
          class="withdrawable-income"
          data-analytics="studio.income_open"
          :aria-label="$t('studioBusiness.viewIncome')"
          @click="switchType('income')"
        >
          <i class="el-icon-wallet" aria-hidden="true" />
          <span>
            <small>{{ $t("studioBusiness.withdrawableIncome") }}</small>
            <strong v-if="incomeSummaryReady">{{ coinAmount(incomeSummary.total) }} <em>{{ $t("wallet.coins") }}</em></strong>
            <strong v-else><i class="el-icon-loading" :aria-label="$t('common.loading')" /></strong>
          </span>
          <b>{{ $t("studioBusiness.viewIncome") }} <i class="el-icon-arrow-right" aria-hidden="true" /></b>
        </button>

        <div
          class="studio-tabs-shell"
          :class="{ 'can-scroll-left': tabScroll.left, 'can-scroll-right': tabScroll.right }"
        >
          <nav
            ref="studioTabs"
            class="studio-tabs"
            :aria-label="$t('studioBusiness.navigation')"
            @scroll.passive="updateTabScrollState"
          >
            <button
              v-for="tab in tabs"
              :key="tab.code"
              :data-studio-tab="tab.code"
              :data-analytics="`studio.tab.${tab.code}`"
              :class="{ active: activeType === tab.code }"
              @click="switchType(tab.code)"
            >
              <i :class="tab.icon" /><span>{{ $t(tab.labelKey) }}</span>
            </button>
          </nav>
          <button
            v-show="tabScroll.left"
            type="button"
            class="studio-tab-scroll previous"
            :aria-label="$t('studioBusiness.navigation')"
            data-analytics="studio.tab_previous"
            @click="scrollTabs(-1)"
          ><i class="el-icon-arrow-left" /></button>
          <button
            v-show="tabScroll.right"
            type="button"
            class="studio-tab-scroll next"
            :aria-label="$t('studioBusiness.navigation')"
            data-analytics="studio.tab_next"
            @click="scrollTabs(1)"
          ><i class="el-icon-arrow-right" /></button>
        </div>

        <fan-group-manager v-if="activeType === 'fan_group'" @open-chat="$emit('open-fan-group', $event)" />

        <template v-if="activeType !== 'income' && activeType !== 'fan_group'">
          <button v-if="hasActiveRole" :class="['create-work', { 'stop-live': activeType === 'live' && hasActiveLive }]" :disabled="liveEnding" data-analytics="studio.create_action" @click="openCreateAction">
            <i :class="activeType === 'live' && hasActiveLive ? 'el-icon-switch-button' : 'el-icon-plus'" />{{ createLabel }}
          </button>
          <button v-else-if="activeType === 'live'" class="apply-role" data-analytics="studio.apply_streamer" @click="applyActiveRole">
            <i class="el-icon-unlock" />{{ $t("studioUi.applyStreamerAction") }}
          </button>
        </template>

        <section v-if="listLoading && !items.length" class="studio-loading"><i class="el-icon-loading" />{{ $t("common.loading") }}</section>

        <section v-else-if="isWorkType && items.length" class="work-list">
          <article
            v-for="work in items"
            :key="work.id"
            :class="['work-card', { previewable: isWorkOpenable(work) }]"
            :role="isWorkOpenable(work) ? 'button' : null"
            :tabindex="isWorkOpenable(work) ? 0 : null"
            :aria-label="isWorkOpenable(work) ? $t('studioPreview.open', { title: work.title }) : null"
            data-analytics="studio.work_open"
            @click="openWork(work)"
            @keydown.enter.prevent="openWork(work)"
          >
            <div class="work-cover">
              <img v-if="work.cover_url" :src="work.cover_url" :alt="work.title" loading="lazy">
              <i v-else :class="work.content_type === 'live' ? 'el-icon-microphone' : 'el-icon-video-camera'" />
              <span v-if="work.live_status === 1">{{ $t("studioUi.liveNow") }}</span>
              <b v-if="isWorkOpenable(work)" class="preview-mark" aria-hidden="true"><i class="el-icon-video-play" /></b>
            </div>
            <div class="work-copy">
              <div><span :class="reviewStateClass(work)">{{ reviewText(work) }}</span><small>#{{ work.id }}</small></div>
              <h3>{{ work.title }}</h3>
              <p v-if="Number(work.review_status) === 3">{{ $t("studioUi.rejectReason", { reason: work.review_note || $t("studioUi.noReason") }) }}</p>
              <p v-else>{{ workStatusText(work) }} · {{ $d(work.update_time, { dateStyle: "medium", timeStyle: "short" }) }}</p>
              <footer @click.stop>
                <button class="primary manage-entry" data-analytics="studio.manage_open" @click="openManager(work)">
                  <i :class="work.content_type === 'live' ? 'el-icon-present' : 'el-icon-setting'" />
                  {{ $t(work.content_type === "live" ? "studioManager.giftDetail" : "studioManager.manage") }}
                </button>
                <button
                  v-if="work.content_type === 'live' && Number(work.live_status) === 1"
                  class="live-stop"
                  :disabled="liveEnding"
                  data-analytics="studio.live_end"
                  @click="toggleItem(work)"
                ><i class="el-icon-switch-button" />{{ $t("studioUi.stopLive") }}</button>
              </footer>
            </div>
          </article>
        </section>

        <section v-else-if="isBusinessType && items.length" class="business-list">
          <article
            v-for="item in items"
            :key="item.id"
            :class="['business-card', { previewable: activeType === 'paid' }]"
            :role="activeType === 'paid' ? 'button' : null"
            :tabindex="activeType === 'paid' ? 0 : null"
            :aria-label="activeType === 'paid' ? $t('studioPreview.open', { title: item.title }) : null"
            data-analytics="studio.business_open"
            @click="activeType === 'paid' && openPreview(item)"
            @keydown.enter.prevent="activeType === 'paid' && openPreview(item)"
          >
            <div class="business-visual" :class="item.theme || 'scene-rain'">
              <img v-if="item.cover_url" :src="item.cover_url" :alt="item.title" loading="lazy">
              <i v-else :class="activeType === 'tip' ? 'el-icon-coffee-cup' : item.content_type === 'video' ? 'el-icon-video-play' : 'el-icon-picture-outline'" />
              <b v-if="activeType === 'paid'" class="preview-mark" aria-hidden="true"><i :class="item.content_type === 'video' ? 'el-icon-video-play' : 'el-icon-picture-outline'" /></b>
            </div>
            <div class="business-copy">
              <header><span :class="reviewStateClass(item)">{{ reviewText(item) }}</span><em>{{ $n(item.coin_price) }} {{ $t("wallet.coins") }}</em></header>
              <h3>{{ item.title }}</h3>
              <p v-if="Number(item.review_status) === 3">{{ $t("studioUi.rejectReason", { reason: item.review_note || $t("studioUi.noReason") }) }}</p>
              <p v-else>{{ activeType === "tip" ? item.description : item.introduction }}</p>
              <footer @click.stop>
                <button v-if="editable(item)" data-analytics="studio.item_edit" @click="openEditor(item)">{{ $t("studioUi.edit") }}</button>
                <button v-if="editable(item)" class="primary" data-analytics="studio.item_submit" @click="submitItem(item)">{{ $t("studioUi.submitReview") }}</button>
                <button v-if="Number(item.review_status) === 2" data-analytics="studio.item_toggle" @click="toggleItem(item)">{{ $t(Number(item.status) === 1 ? "studioUi.hide" : "studioUi.republish") }}</button>
                <button v-if="editable(item)" class="danger" data-analytics="studio.item_delete" @click="removeItem(item)">{{ $t("studioUi.delete") }}</button>
              </footer>
            </div>
          </article>
        </section>

        <section v-else-if="activeType === 'income'" class="income-panel">
          <div class="income-total">
            <span><small>{{ $t("studioBusiness.totalIncome") }}</small><strong>{{ coinAmount(incomeSummary.total) }}</strong><em>{{ $t("wallet.coins") }}</em></span>
            <i class="el-icon-wallet" />
          </div>
          <div class="income-breakdown">
            <span><small>{{ $t("studioBusiness.giftIncome") }}</small><strong>{{ coinAmount(incomeSummary.gift) }}</strong></span>
            <span><small>{{ $t("studioBusiness.tipIncome") }}</small><strong>{{ coinAmount(incomeSummary.tip) }}</strong></span>
            <span><small>{{ $t("studioBusiness.paidIncome") }}</small><strong>{{ coinAmount(incomeSummary.paid) }}</strong></span>
          </div>
          <div v-if="items.length" class="income-list">
            <article v-for="row in items" :key="`${row.income_type}-${row.id}`">
              <i :class="incomeIcon(row.income_type)" />
              <span><strong>{{ row.item_name || $t("studioBusiness.unknownItem") }}</strong><small>{{ $t("studioBusiness.fromUser", { name: row.payer_name || $t("studioBusiness.anonymous") }) }} · {{ $d(row.create_time, { dateStyle: "medium", timeStyle: "short" }) }}</small></span>
              <em>+{{ coinAmount(row.coin_amount) }}</em>
            </article>
          </div>
          <section v-else class="studio-empty compact"><i class="el-icon-coin" /><h3>{{ $t("studioBusiness.noIncome") }}</h3><p>{{ $t("studioBusiness.incomeRule") }}</p></section>
        </section>

        <section v-else-if="activeType !== 'fan_group'" class="studio-empty">
          <i :class="emptyIcon" /><h3>{{ emptyTitle }}</h3><p>{{ $t("studioBusiness.emptyTip") }}</p>
        </section>

        <button v-if="pagination.hasMore" class="load-more" :disabled="listLoading" data-analytics="studio.load_more" @click="loadActive(false)">{{ $t(listLoading ? "studioUi.loadingMore" : "studioUi.loadMore") }}</button>
      </template>
    </main>

    <section v-if="editorVisible" class="studio-editor">
      <header>
        <button type="button" :aria-label="$t('a11y.back')" data-analytics="studio.editor_close" @click="closeEditor"><i class="el-icon-back" /></button>
        <div><small>{{ form.id ? $t("studioUi.editWork") : $t("studioUi.newWork") }}</small><h2>{{ editorTitle }}</h2></div>
        <span />
      </header>
      <div class="editor-scroll">
        <template v-if="isWorkType">
          <div v-if="form.content_type === 'video'" class="source-presets">
            <small>{{ $t("studioUi.sampleAssets") }}</small>
            <button v-for="preset in presets" :key="preset.video" type="button" data-analytics="studio.preset_use" @click="usePreset(preset)">{{ preset.label }}</button>
          </div>
          <el-form label-position="top">
            <div v-if="form.content_type === 'video'" class="media-upload-grid">
              <article>
                <input ref="videoFileInput" class="sr-only" type="file" accept="video/mp4,video/webm,video/quicktime" @change="handleVideoFile">
                <button type="button" :disabled="videoUploading" data-analytics="studio.video_upload" @click="$refs.videoFileInput.click()">
                  <i :class="videoUploading ? 'el-icon-loading' : 'el-icon-video-camera'" />
                  <span><strong>{{ $t(videoUploading ? "studioUi.uploadingVideo" : "studioUi.chooseVideo") }}</strong><small>{{ videoUploadLabel || $t("studioUi.videoUploadTip") }}</small></span>
                  <em v-if="videoUploading">{{ videoUploadProgress }}%</em><i v-else class="el-icon-arrow-right" />
                </button>
              </article>
              <article>
                <input ref="coverFileInput" class="sr-only" type="file" accept="image/jpeg,image/png,image/webp" @change="handleCoverFile">
                <button type="button" :disabled="coverUploading" data-analytics="studio.cover_upload" @click="$refs.coverFileInput.click()">
                  <i :class="coverUploading ? 'el-icon-loading' : 'el-icon-picture-outline'" />
                  <span><strong>{{ $t(coverUploading ? "studioUi.uploadingCover" : "studioUi.chooseCover") }}</strong><small>{{ coverUploadLabel || $t("studioUi.coverUploadTip") }}</small></span>
                  <em v-if="coverUploading">{{ coverUploadProgress }}%</em><i v-else class="el-icon-arrow-right" />
                </button>
              </article>
            </div>
            <el-form-item :label="$t('studioUi.workTitle')"><el-input v-model.trim="form.title" maxlength="120" show-word-limit :placeholder="$t('studioUi.titlePlaceholder')" /></el-form-item>
            <el-form-item :label="$t('studioUi.description')"><el-input v-model.trim="form.description" type="textarea" :rows="3" maxlength="500" show-word-limit :placeholder="$t('studioUi.descriptionPlaceholder')" /></el-form-item>
            <el-form-item :label="$t('studioUi.tags')"><el-input v-model.trim="form.tags" maxlength="255" :placeholder="$t('studioUi.tagsPlaceholder')" /></el-form-item>
            <el-form-item v-if="form.content_type === 'video'" :label="$t('studioUi.playUrl')"><el-input v-model.trim="form.video_url" maxlength="500" placeholder="https://... / MP4" /></el-form-item>
            <el-form-item :label="$t('studioUi.coverUrl')"><el-input v-model.trim="form.cover_url" maxlength="500" placeholder="https://... /cover.webp" /></el-form-item>
            <el-form-item v-if="form.content_type === 'video'" :label="$t('studioUi.discoveryMusic')">
              <el-select v-model="form.music_id" clearable filterable :placeholder="$t('studioUi.chooseMusic')" @change="selectMusic">
                <el-option v-for="track in musicOptions" :key="track.id" :label="`${track.title} · ${track.artist}`" :value="Number(track.id)" />
              </el-select>
              <div v-if="selectedMusic" class="selected-music">
                <img :src="selectedMusic.cover_url" :alt="selectedMusic.title">
                <span><strong>{{ selectedMusic.title }}</strong><small>{{ selectedMusic.artist }} · {{ formatDuration(selectedMusic.duration) }}</small></span>
                <i class="el-icon-headset" />
              </div>
            </el-form-item>
            <div class="form-pair">
              <el-form-item :label="$t('studioUi.duration')"><el-input-number v-model="form.duration" :min="0" :max="86400" /></el-form-item>
              <el-form-item :label="$t('studioUi.visibility')"><el-select v-model="form.permission"><el-option :label="$t('studioUi.public')" value="public" /><el-option :label="$t('studioUi.loginOnly')" value="login" /><el-option :label="$t('studioUi.private')" value="private" /></el-select></el-form-item>
            </div>
            <el-form-item v-if="form.content_type === 'video'" :label="$t('studioComments.setting')">
              <el-select v-model="form.comments_enabled">
                <el-option :label="$t('studioComments.allowed')" :value="1" />
                <el-option :label="$t('studioComments.disabled')" :value="0" />
              </el-select>
            </el-form-item>
          </el-form>
        </template>

        <el-form v-else-if="activeType === 'tip'" label-position="top">
          <el-form-item :label="$t('studioBusiness.title')"><el-input v-model.trim="form.title" maxlength="80" show-word-limit :placeholder="$t('studioBusiness.tipTitlePlaceholder')" /></el-form-item>
          <el-form-item :label="$t('studioBusiness.description')"><el-input v-model.trim="form.description" type="textarea" :rows="4" maxlength="300" show-word-limit :placeholder="$t('studioBusiness.tipDescriptionPlaceholder')" /></el-form-item>
          <div class="form-pair">
            <el-form-item :label="$t('studioBusiness.coinPrice')"><el-input-number v-model="form.coin_price" :min="1" :max="1000000" /></el-form-item>
            <el-form-item :label="$t('studioBusiness.icon')"><el-select v-model="form.icon"><el-option v-for="icon in tipIcons" :key="icon" :label="$t(`studioBusiness.icon_${icon}`)" :value="icon" /></el-select></el-form-item>
          </div>
        </el-form>

        <el-form v-else label-position="top">
          <section class="paid-upload-panel">
            <header>
              <div>
                <small>{{ $t("studioBusiness.localUpload") }}</small>
                <strong>{{ $t(form.content_type === "album" ? "studioBusiness.uploadAlbumAssets" : "studioBusiness.uploadVideoAsset") }}</strong>
              </div>
              <em>{{ $t(form.content_type === "album" ? "studioBusiness.albumLimit" : "studioBusiness.videoLimit") }}</em>
            </header>
            <div class="media-upload-grid paid-media-upload">
              <article>
                <input
                  ref="paidAssetFileInput"
                  class="sr-only"
                  type="file"
                  :accept="paidAssetAccept"
                  :multiple="form.content_type === 'album'"
                  @change="handlePaidAssetFiles"
                >
                <button type="button" :disabled="paidAssetUploading" data-analytics="studio.paid_asset_upload" @click="$refs.paidAssetFileInput.click()">
                  <i :class="paidAssetUploading ? 'el-icon-loading' : form.content_type === 'album' ? 'el-icon-picture-outline' : 'el-icon-video-camera'" />
                  <span>
                    <strong>{{ $t(paidAssetUploading ? "studioBusiness.uploadingAssets" : "studioBusiness.chooseAssets") }}</strong>
                    <small>{{ paidAssetUploadLabel || $t("studioBusiness.chooseAssetsTip") }}</small>
                  </span>
                  <em v-if="paidAssetUploading">{{ paidAssetUploadProgress }}%</em><i v-else class="el-icon-upload2" />
                </button>
              </article>
              <article>
                <input ref="paidCoverFileInput" class="sr-only" type="file" accept="image/jpeg,image/png,image/webp" @change="handleCoverFile">
                <button type="button" :disabled="coverUploading" data-analytics="studio.paid_cover_upload" @click="$refs.paidCoverFileInput.click()">
                  <i :class="coverUploading ? 'el-icon-loading' : 'el-icon-picture'" />
                  <span>
                    <strong>{{ $t(coverUploading ? "studioUi.uploadingCover" : "studioBusiness.choosePaidCover") }}</strong>
                    <small>{{ coverUploadLabel || $t("studioBusiness.paidCoverTip") }}</small>
                  </span>
                  <em v-if="coverUploading">{{ coverUploadProgress }}%</em><i v-else class="el-icon-upload2" />
                </button>
              </article>
            </div>
            <div v-if="paidAssetUrls.length" class="paid-asset-list">
              <article v-for="(asset, index) in paidAssetUrls" :key="`${asset}-${index}`">
                <img v-if="form.content_type === 'album'" :src="asset" :alt="$t('studioBusiness.assetIndex', { index: index + 1 })">
                <span v-else><i class="el-icon-video-play" /></span>
                <div>
                  <strong>{{ $t("studioBusiness.assetIndex", { index: index + 1 }) }}</strong>
                </div>
                <button type="button" :aria-label="$t('studioBusiness.removeAsset')" data-analytics="studio.paid_asset_remove" @click="removePaidAsset(index)"><i class="el-icon-close" /></button>
              </article>
            </div>
          </section>
          <el-form-item :label="$t('studioBusiness.title')"><el-input v-model.trim="form.title" maxlength="120" show-word-limit :placeholder="$t('studioBusiness.paidTitlePlaceholder')" /></el-form-item>
          <el-form-item :label="$t('studioBusiness.description')"><el-input v-model.trim="form.introduction" type="textarea" :rows="4" maxlength="500" show-word-limit :placeholder="$t('studioBusiness.paidDescriptionPlaceholder')" /></el-form-item>
          <div class="form-pair">
            <el-form-item :label="$t('studioBusiness.contentType')"><el-select v-model="form.content_type"><el-option :label="$t('appUi.paidAlbum')" value="album" /><el-option :label="$t('appUi.paidVideo')" value="video" /></el-select></el-form-item>
            <el-form-item :label="$t('studioBusiness.coinPrice')"><el-input-number v-model="form.coin_price" :min="1" :max="1000000" /></el-form-item>
          </div>
        </el-form>

        <p class="editor-note"><i class="el-icon-info" />{{ isWorkType ? $t(form.content_type === "live" ? "studioUi.liveSourceTip" : "studioUi.reviewTip") : $t("studioBusiness.reviewTip") }}</p>
        <div class="editor-actions"><button type="button" :disabled="saving || videoUploading || coverUploading || paidAssetUploading" data-analytics="studio.draft_save" @click="saveDraft(false)">{{ $t("studioUi.saveDraft") }}</button><button type="button" class="primary" :disabled="saving || videoUploading || coverUploading || paidAssetUploading" data-analytics="studio.draft_submit" @click="saveDraft(true)">{{ $t(saving ? "studioUi.saving" : "studioUi.saveAndSubmit") }}</button></div>
      </div>
    </section>

    <!-- 开播只收集 TikTok ID；播放流、标题和直播封面均由服务端读取 TikTok 房间信息。 -->
    <section v-if="liveStartVisible" class="studio-live-start" role="dialog" aria-modal="true" :aria-label="$t('studioUi.tiktokStartTitle')">
      <button type="button" class="live-start-backdrop" :aria-label="$t('common.cancel')" data-analytics="studio.live_dialog_close" @click="closeLiveStart" />
      <div class="live-start-card">
        <header>
          <span><i class="el-icon-microphone" /></span>
          <div><small>STARLIVE LIVE</small><h2>{{ $t("studioUi.tiktokStartTitle") }}</h2></div>
          <button type="button" :aria-label="$t('common.cancel')" :disabled="liveStarting" data-analytics="studio.live_dialog_close" @click="closeLiveStart"><i class="el-icon-close" /></button>
        </header>
        <p>{{ $t("studioUi.tiktokStartDescription") }}</p>
        <el-form label-position="top" @submit.native.prevent>
          <el-form-item :label="$t('studioUi.tiktokId')">
            <el-input
              v-model.trim="liveStartTikTokId"
              maxlength="25"
              autocomplete="off"
              :placeholder="$t('studioUi.tiktokIdPlaceholder')"
              @keyup.enter.native="confirmLiveStart"
            ><template slot="prepend">@</template></el-input>
          </el-form-item>
        </el-form>
        <div class="live-start-note"><i class="el-icon-info" /><span>{{ $t("studioUi.tiktokStartTip") }}</span></div>
        <div class="live-start-actions">
          <button type="button" :disabled="liveStarting" data-analytics="studio.live_dialog_close" @click="closeLiveStart">{{ $t("common.cancel") }}</button>
          <button type="button" class="primary" :disabled="liveStarting || !liveStartTikTokId" data-analytics="studio.live_start" @click="confirmLiveStart">
            <i :class="liveStarting ? 'el-icon-loading' : 'el-icon-video-play'" />
            {{ $t(liveStarting ? "studioUi.tiktokChecking" : "studioUi.confirmStartLive") }}
          </button>
        </div>
      </div>
    </section>

    <!-- 作品操作统一收口到管理子页，避免目录卡片堆叠按钮，并为后续功能保留扩展位置。 -->
    <section v-if="managerVisible && managerItem" class="studio-manager">
      <header>
        <button type="button" :aria-label="$t('a11y.back')" data-analytics="studio.manager_close" @click="closeManager"><i class="el-icon-back" /></button>
        <div><small>{{ $t("studioManager.eyebrow") }}</small><h2>{{ $t("studioManager.title") }}</h2></div>
        <span />
      </header>
      <div class="manager-scroll">
        <article class="manager-work">
          <div>
            <img v-if="managerItem.cover_url" :src="managerItem.cover_url" :alt="managerItem.title">
            <i v-else :class="managerItem.content_type === 'live' ? 'el-icon-microphone' : 'el-icon-video-camera'" />
          </div>
          <section>
            <span :class="reviewStateClass(managerItem)">{{ reviewText(managerItem) }}</span>
            <h3>{{ managerItem.title }}</h3>
            <p>{{ workStatusText(managerItem) }} · #{{ managerItem.id }}</p>
          </section>
        </article>

        <div class="manager-actions">
          <button type="button" data-analytics="studio.manager_preview" @click="openManagerPreview">
            <i class="el-icon-video-play" />
            <span><strong>{{ $t("studioManager.preview") }}</strong><small>{{ $t("studioManager.previewTip") }}</small></span>
            <i class="el-icon-arrow-right" />
          </button>
          <button v-if="managerItem.content_type === 'video'" type="button" data-analytics="studio.manager_comments" @click="openManagerComments">
            <i class="el-icon-chat-dot-round" />
            <span><strong>{{ $t("studioManager.comments") }}</strong><small>{{ $t("studioManager.commentsTip", { count: $n(Number(managerItem.comment_count || 0)) }) }}</small></span>
            <i class="el-icon-arrow-right" />
          </button>
          <button v-if="managerItem.content_type === 'video'" type="button" data-analytics="studio.manager_comments_toggle" @click="updateManagedComments">
            <i :class="managedCommentsEnabled ? 'el-icon-chat-line-round' : 'el-icon-turn-off'" />
            <span><strong>{{ $t("studioComments.setting") }}</strong><small>{{ $t(managedCommentsEnabled ? "studioManager.commentsAllowedTip" : "studioManager.commentsDisabledTip") }}</small></span>
            <em :class="{ enabled: managedCommentsEnabled }">{{ $t(managedCommentsEnabled ? "studioComments.allowed" : "studioComments.disabled") }}</em>
          </button>
          <button v-if="managerItem.content_type === 'video' && editable(managerItem)" type="button" data-analytics="studio.manager_edit" @click="editManagedItem">
            <i class="el-icon-edit-outline" />
            <span><strong>{{ $t("studioManager.editContent") }}</strong><small>{{ $t("studioManager.editContentTip") }}</small></span>
            <i class="el-icon-arrow-right" />
          </button>
          <button v-if="managerItem.content_type === 'video' && editable(managerItem)" type="button" data-analytics="studio.manager_submit" @click="submitManagedItem">
            <i class="el-icon-upload2" />
            <span><strong>{{ $t("studioUi.submitReview") }}</strong><small>{{ $t("studioManager.submitTip") }}</small></span>
            <i class="el-icon-arrow-right" />
          </button>
          <button v-if="managerItem.content_type === 'video' && Number(managerItem.review_status) === 2" type="button" data-analytics="studio.manager_toggle" @click="toggleManagedItem">
            <i :class="Number(managerItem.status) === 1 ? 'el-icon-video-pause' : 'el-icon-video-play'" />
            <span><strong>{{ $t(Number(managerItem.status) === 1 ? "studioUi.hide" : "studioUi.republish") }}</strong><small>{{ $t(Number(managerItem.status) === 1 ? "studioManager.hideTip" : "studioManager.republishTip") }}</small></span>
            <i class="el-icon-arrow-right" />
          </button>
          <button v-if="managerItem.content_type === 'live' && Number(managerItem.live_status) === 1" type="button" data-analytics="studio.manager_end_live" @click="toggleManagedItem">
            <i class="el-icon-switch-button" />
            <span><strong>{{ $t("studioUi.endLive") }}</strong><small>{{ $t("studioManager.endLiveTip") }}</small></span>
            <i class="el-icon-arrow-right" />
          </button>
          <button v-if="managerItem.content_type === 'video' && editable(managerItem)" type="button" class="danger" data-analytics="studio.manager_delete" @click="removeManagedItem">
            <i class="el-icon-delete" />
            <span><strong>{{ $t("studioUi.delete") }}</strong><small>{{ $t("studioManager.deleteTip") }}</small></span>
            <i class="el-icon-arrow-right" />
          </button>
        </div>
      </div>
    </section>

    <!-- 直播管理直接展示当前场次的真实礼物流水，不与其他直播或经营收入混合。 -->
    <section v-if="liveGiftVisible && liveGiftWork" class="studio-manager live-gift-detail" role="dialog" aria-modal="true" :aria-label="$t('studioManager.giftDetail')">
      <header>
        <button type="button" :aria-label="$t('a11y.back')" data-analytics="studio.gifts_close" @click="closeLiveGiftDetail"><i class="el-icon-back" /></button>
        <div><small>{{ $t("studioManager.giftDetailEyebrow") }}</small><h2>{{ $t("studioManager.giftDetail") }}</h2></div>
        <span />
      </header>
      <div class="manager-scroll">
        <article class="manager-work live-gift-work">
          <div>
            <img v-if="liveGiftWork.cover_url" :src="liveGiftWork.cover_url" :alt="liveGiftWork.title">
            <i v-else class="el-icon-microphone" />
          </div>
          <section>
            <span :class="reviewStateClass(liveGiftWork)">{{ reviewText(liveGiftWork) }}</span>
            <h3>{{ liveGiftWork.title }}</h3>
            <p>#{{ liveGiftWork.id }} · {{ workStatusText(liveGiftWork) }}</p>
          </section>
          <button
            v-if="Number(liveGiftWork.live_status) === 1"
            type="button"
            class="gift-stop-live"
            :disabled="liveEnding"
            data-analytics="studio.gifts_end_live"
            @click="endLiveFromGiftDetail"
          ><i class="el-icon-switch-button" />{{ $t("studioUi.stopLive") }}</button>
        </article>

        <section class="live-gift-summary">
          <span><small>{{ $t("studioManager.giftCoins") }}</small><strong>{{ coinAmount(liveGiftSummary.coins) }}</strong></span>
          <span><small>{{ $t("studioManager.giftOrders") }}</small><strong>{{ $n(liveGiftSummary.orders) }}</strong></span>
          <span><small>{{ $t("studioManager.giftQuantity") }}</small><strong>{{ $n(liveGiftSummary.quantity) }}</strong></span>
        </section>

        <div v-if="liveGiftRows.length" class="live-gift-list">
          <article v-for="row in liveGiftRows" :key="row.id">
            <platform-avatar :src="row.payer_avatar_url" :alt="row.payer_name || $t('studioBusiness.anonymous')" />
            <span>
              <strong>{{ row.payer_name || $t("studioBusiness.anonymous") }}</strong>
              <small>{{ $t("studioManager.giftItem", { gift: row.item_name || $t("studioBusiness.unknownItem"), count: $n(row.quantity) }) }}</small>
              <time>{{ $d(row.create_time, { dateStyle: "medium", timeStyle: "short" }) }}</time>
            </span>
            <em>+{{ coinAmount(row.coin_amount) }}</em>
          </article>
        </div>
        <section v-else-if="!liveGiftLoading" class="studio-empty compact live-gift-empty">
          <i class="el-icon-present" />
          <h3>{{ $t("studioManager.noLiveGifts") }}</h3>
          <p>{{ $t("studioManager.noLiveGiftsTip") }}</p>
        </section>
        <section v-else class="studio-loading live-gift-loading"><i class="el-icon-loading" />{{ $t("common.loading") }}</section>

        <button v-if="liveGiftPagination.hasMore" class="load-more" :disabled="liveGiftLoading" data-analytics="studio.gifts_load_more" @click="loadLiveGiftDetail(false)">
          {{ $t(liveGiftLoading ? "studioUi.loadingMore" : "studioUi.loadMore") }}
        </button>
      </div>
    </section>

    <!-- 创作者可以在不改变审核和上架状态的前提下预览自己的原始内容。 -->
    <section v-if="previewVisible && previewItem" class="studio-preview" role="dialog" aria-modal="true" :aria-label="previewTitle">
      <header>
        <button type="button" :aria-label="$t('studioPreview.close')" data-analytics="studio.preview_close" @click="closePreview"><i class="el-icon-back" /></button>
        <div><small>{{ previewEyebrow }}</small><h2>{{ previewItem.title }}</h2></div>
        <span :class="reviewStateClass(previewItem)">{{ reviewText(previewItem) }}</span>
      </header>
      <div class="preview-scroll">
        <div v-if="previewKind === 'album'" class="album-preview">
          <div class="preview-stage">
            <img
              :src="previewAssets[previewImageIndex]"
              :alt="$t('studioPreview.imageAlt', { title: previewItem.title, index: previewImageIndex + 1 })"
              :draggable="false"
              @click="openCurrentPreviewImage"
              @contextmenu.prevent
            >
            <button
              type="button"
              class="preview-arrow previous"
              :disabled="previewImageIndex === 0"
              :aria-label="$t('studioPreview.previous')"
              data-analytics="studio.preview_previous"
              @click="stepPreview(-1)"
            ><i class="el-icon-arrow-left" /></button>
            <button
              type="button"
              class="preview-arrow next"
              :disabled="previewImageIndex >= previewAssets.length - 1"
              :aria-label="$t('studioPreview.next')"
              data-analytics="studio.preview_next"
              @click="stepPreview(1)"
            ><i class="el-icon-arrow-right" /></button>
          </div>
          <div class="album-toolbar">
            <span>{{ $t("studioPreview.imageCount", { current: previewImageIndex + 1, total: previewAssets.length }) }}</span>
            <div>
              <button
                v-for="(asset, index) in previewAssets"
                :key="`${asset}-${index}`"
                type="button"
                :class="{ active: previewImageIndex === index }"
                :aria-label="$t('studioPreview.goToImage', { index: index + 1 })"
                data-analytics="studio.preview_select_image"
                @click="previewImageIndex = index"
              ><img :src="asset" alt=""></button>
            </div>
          </div>
        </div>

        <div v-else class="video-preview">
          <video
            v-if="!previewVideoError"
            ref="previewVideo"
            :src="previewVideoUrl"
            :poster="previewItem.cover_url || ''"
            controls
            autoplay
            playsinline
            webkit-playsinline
            @error="previewVideoError = true"
          />
          <div v-else class="preview-error">
            <i class="el-icon-warning-outline" />
            <h3>{{ $t("studioPreview.playFailed") }}</h3>
            <p>{{ $t("studioPreview.playFailedTip") }}</p>
            <button type="button" data-analytics="studio.preview_retry" @click="retryPreviewVideo">{{ $t("studioPreview.retry") }}</button>
          </div>
        </div>

        <article class="preview-info">
          <div><span>{{ previewTypeLabel }}</span><em v-if="previewSourceType === 'paid'">{{ $n(previewItem.coin_price) }} {{ $t("wallet.coins") }}</em></div>
          <p v-if="previewDescription">{{ previewDescription }}</p>
          <small><i class="el-icon-view" />{{ $t("studioPreview.ownerNotice") }}</small>
        </article>

        <section v-if="previewSourceType === 'work' && previewItem.content_type === 'video'" ref="studioComments" class="studio-comments">
          <header>
            <div>
              <small>{{ $t("studioComments.eyebrow") }}</small>
              <h3>{{ $t("studioComments.title", { count: $n(Number(previewItem.comment_count || 0)) }) }}</h3>
            </div>
          </header>

          <div v-if="previewCommentsLoading" class="studio-comment-state"><i class="el-icon-loading" />{{ $t("common.loading") }}</div>
          <div v-else-if="previewThreadedComments.length" class="studio-comment-list">
            <article v-for="comment in previewThreadedComments" :key="comment.id" :class="{ reply: Number(comment.parent_id) > 0 }">
              <avatar-with-frame class="studio-comment-avatar" :avatar="comment.avatar_url" :avatar-alt="comment.user_name" :frame-animation-url="comment.avatar_frame_url" :frame-preview-url="comment.avatar_frame_preview_url" />
              <div>
                <strong>{{ comment.user_name }} <em v-if="comment.reply_to_name">{{ $t("common.reply") }} {{ comment.reply_to_name }}</em></strong>
                <p>{{ comment.content }}</p>
                <footer>
                  <small>{{ $d(comment.create_time, { dateStyle: "medium", timeStyle: "short" }) }}</small>
                  <button v-if="previewCommentsEnabled" type="button" data-analytics="studio.comment_reply" @click="startPreviewReply(comment)">{{ $t("appUi.reply") }}</button>
                  <button type="button" class="danger" data-analytics="studio.comment_delete" @click="removePreviewComment(comment)">{{ $t("studioComments.remove") }}</button>
                </footer>
              </div>
            </article>
          </div>
          <div v-else class="studio-comment-state"><i class="el-icon-chat-line-square" />{{ $t("appUi.noComments") }}</div>

          <div v-if="previewCommentsEnabled" class="studio-comment-editor">
            <div v-if="previewReplying" class="studio-replying">
              <span>{{ $t("appUi.replyTo", { name: previewReplying.user_name }) }}</span>
              <button type="button" data-analytics="studio.comment_reply_cancel" @click="cancelPreviewReply">{{ $t("common.cancel") }}</button>
            </div>
            <div>
              <el-input
                v-model.trim="previewCommentText"
                maxlength="300"
                :placeholder="previewReplying ? $t('appUi.replyPlaceholder', { name: previewReplying.user_name }) : $t('studioComments.creatorPlaceholder')"
                @keyup.enter.native="postPreviewComment"
              />
              <button type="button" :disabled="previewCommentSaving || !previewCommentText" data-analytics="studio.comment_send" @click="postPreviewComment">{{ $t("appUi.send") }}</button>
            </div>
          </div>
          <p v-else class="studio-comments-disabled"><i class="el-icon-turn-off" />{{ $t("studioComments.disabledHint") }}</p>
        </section>
      </div>
    </section>
  </section>
</template>

<script>
import {
  addComment,
  deleteComment,
  getComments
} from "../api/content"
import {
  deleteStudioPaid,
  deleteStudioTip,
  deleteStudioWork,
  getStudioIncome,
  getStudioLiveGifts,
  getStudioOverview,
  getStudioPaid,
  getStudioTips,
  getStudioWorks,
  saveStudioPaid,
  saveStudioTip,
  saveStudioWork,
  submitStudioPaid,
  submitStudioTip,
  submitStudioWork,
  startStudioLive,
  updateStudioLiveStatus,
  updateStudioWorkCommentStatus,
  updateStudioPaidStatus,
  updateStudioTipStatus,
  updateStudioWorkStatus
} from "../api/studio"
import { getMusicList } from "../api/discover"
import { uploadMedia } from "../api/upload"
import { platformMediaUrl } from "../utils/media"
import { openImageViewer } from "../utils/imageViewer"
import AvatarWithFrame from "./AvatarWithFrame.vue"
import FanGroupManager from "./FanGroupManager.vue"

export default {
  name: "CreatorStudio",
  components: { AvatarWithFrame, FanGroupManager },
  props: { embedded: { type: Boolean, default: false } },
  data() {
    return {
      overview: null,
      loading: false,
      listLoading: false,
      saving: false,
      activeType: "live",
      items: [],
      incomeSummary: { total: 0, gift: 0, tip: 0, paid: 0 },
      incomeSummaryReady: false,
      pagination: { page: 0, hasMore: false },
      editorVisible: false,
      managerVisible: false,
      managerItem: null,
      liveGiftVisible: false,
      liveGiftWork: null,
      liveGiftRows: [],
      liveGiftLoading: false,
      liveGiftRequestId: 0,
      liveGiftSummary: { coins: 0, orders: 0, quantity: 0 },
      liveGiftPagination: { page: 0, hasMore: false },
      previewVisible: false,
      previewItem: null,
      previewSourceType: "",
      previewImageIndex: 0,
      previewVideoError: false,
      previewComments: [],
      previewCommentsLoading: false,
      previewCommentSaving: false,
      previewCommentText: "",
      previewReplying: null,
      liveStartVisible: false,
      liveStartTikTokId: "",
      liveStarting: false,
      liveEnding: false,
      form: {},
      musicOptions: [],
      musicLoading: false,
      videoUploading: false,
      coverUploading: false,
      videoUploadProgress: 0,
      coverUploadProgress: 0,
      videoUploadLabel: "",
      coverUploadLabel: "",
      tabScroll: { left: false, right: false },
      paidAssetUploading: false,
      paidAssetUploadProgress: 0,
      paidAssetUploadLabel: "",
      tabs: [
        { code: "live", labelKey: "studioUi.liveRooms", icon: "el-icon-microphone" },
        { code: "fan_group", labelKey: "fanGroup.management", icon: "el-icon-user-solid" },
        { code: "tip", labelKey: "studioBusiness.tips", icon: "el-icon-coffee-cup" },
        { code: "paid", labelKey: "studioBusiness.paid", icon: "el-icon-lock" },
        { code: "income", labelKey: "studioBusiness.income", icon: "el-icon-coin" }
      ],
      tipIcons: ["coffee", "flower", "energy", "light", "food", "kitchen", "star"],
      presets: [
        { label: "City Night", video: platformMediaUrl("videos/city-night.mp4"), cover: platformMediaUrl("covers/city-night.webp") },
        { label: "Coastal Rail", video: platformMediaUrl("videos/coastal-rail.mp4"), cover: platformMediaUrl("covers/coastal-rail.webp") },
        { label: "Home Kitchen", video: platformMediaUrl("videos/home-kitchen.mp4"), cover: platformMediaUrl("covers/home-kitchen.webp") }
      ]
    }
  },
  computed: {
    isWorkType() { return this.activeType === "live" },
    isBusinessType() { return ["tip", "paid"].includes(this.activeType) },
    hasActiveRole() {
      return Boolean(this.overview && this.overview.roles.streamer_enabled)
    },
    hasActiveLive() {
      return Boolean(this.activeLiveItem)
    },
    activeLiveItem() {
      if (this.activeType !== "live") return null
      return this.items.find(item => Number(item.live_status) === 1 && Number(item.status) === 1) || null
    },
    createLabel() {
      if (this.activeType === "video") return this.$t("studioUi.publishVideo")
      if (this.activeType === "live") return this.$t(this.hasActiveLive ? "studioUi.stopLive" : "studioUi.startLive")
      if (this.activeType === "tip") return this.$t("studioBusiness.createTip")
      return this.$t("studioBusiness.createPaid")
    },
    editorTitle() {
      if (this.activeType === "video") return this.$t("studioUi.shortVideo")
      if (this.activeType === "live") return this.$t("studioUi.liveRoom")
      if (this.activeType === "tip") return this.$t("studioBusiness.tipProject")
      return this.$t("studioBusiness.paidContent")
    },
    emptyIcon() {
      return { video: "el-icon-video-camera", live: "el-icon-microphone", tip: "el-icon-coffee-cup", paid: "el-icon-lock" }[this.activeType]
    },
    emptyTitle() {
      return this.$t({ video: "studioUi.noVideos", live: "studioUi.noLiveRooms", tip: "studioBusiness.noTips", paid: "studioBusiness.noPaid" }[this.activeType])
    },
    previewAssets() {
      if (!this.previewItem || this.previewSourceType !== "paid") return []
      return this.normalizeAssetUrls(this.previewItem.asset_urls)
    },
    previewKind() {
      return this.previewSourceType === "paid" && this.previewItem && this.previewItem.content_type === "album" ? "album" : "video"
    },
    previewVideoUrl() {
      if (!this.previewItem) return ""
      return this.previewSourceType === "paid" ? (this.previewAssets[0] || "") : (this.previewItem.video_url || "")
    },
    previewDescription() {
      if (!this.previewItem) return ""
      return this.previewSourceType === "paid" ? this.previewItem.introduction : this.previewItem.description
    },
    previewEyebrow() {
      if (this.previewSourceType === "paid") return this.$t(this.previewKind === "album" ? "studioPreview.paidAlbum" : "studioPreview.paidVideo")
      return this.$t(this.previewItem && this.previewItem.content_type === "live" ? "studioPreview.live" : "studioPreview.work")
    },
    previewTitle() {
      return `${this.previewEyebrow}: ${this.previewItem ? this.previewItem.title : ""}`
    },
    previewTypeLabel() {
      if (this.previewSourceType === "paid") return this.$t(this.previewKind === "album" ? "appUi.paidAlbum" : "appUi.paidVideo")
      return this.$t(this.previewItem && this.previewItem.content_type === "live" ? "studioUi.liveRoom" : "studioUi.shortVideos")
    },
    previewCommentsEnabled() {
      return !this.previewItem || Number(this.previewItem.comments_enabled) !== 0
    },
    managedCommentsEnabled() {
      return !this.managerItem || Number(this.managerItem.comments_enabled) !== 0
    },
    selectedMusic() {
      return this.musicOptions.find(track => Number(track.id) === Number(this.form.music_id)) || null
    },
    paidAssetUrls() {
      return this.normalizeAssetUrls(this.form.asset_urls_text)
    },
    paidAssetAccept() {
      return this.form.content_type === "video"
        ? "video/mp4,video/webm,video/quicktime"
        : "image/jpeg,image/png,image/webp"
    },
    previewThreadedComments() {
      // 工作台与首页采用相同的父子顺序，回复始终紧跟对应父评论。
      const comments = this.previewComments.slice()
      const ids = new Set(comments.map(comment => Number(comment.id)))
      const children = new Map()
      const roots = []
      comments.forEach(comment => {
        const parentId = Number(comment.parent_id) || 0
        if (!parentId || !ids.has(parentId)) {
          roots.push(comment)
          return
        }
        if (!children.has(parentId)) children.set(parentId, [])
        children.get(parentId).push(comment)
      })
      roots.sort((left, right) => Number(right.id) - Number(left.id))
      children.forEach(rows => rows.sort((left, right) => Number(left.id) - Number(right.id)))
      const ordered = []
      const visited = new Set()
      const append = comment => {
        const id = Number(comment.id)
        if (visited.has(id)) return
        visited.add(id)
        ordered.push(comment)
        ;(children.get(id) || []).forEach(append)
      }
      roots.forEach(append)
      comments.forEach(append)
      return ordered
    }
  },
  created() { this.initialize() },
  mounted() {
    window.addEventListener("resize", this.handleTabLayoutChange)
    window.addEventListener("locale-changed", this.handleTabLayoutChange)
    this.handleTabLayoutChange()
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.handleTabLayoutChange)
    window.removeEventListener("locale-changed", this.handleTabLayoutChange)
  },
  methods: {
    async initialize() {
      this.loading = true
      try {
        const res = await getStudioOverview()
        this.overview = res.data
        if (!this.overview.roles.streamer_enabled) {
          this.$emit("apply-role", "streamer")
          return
        }
        await Promise.all([
          this.loadActive(true),
          this.loadIncomeSummary()
        ])
      } catch (error) {
        // 请求层已显示主题错误提示；组件初始化失败时保留当前页面，避免开发错误遮罩。
      } finally {
        this.loading = false
        this.handleTabLayoutChange()
      }
    },
    async loadIncomeSummary() {
      try {
        const res = await getStudioIncome({ page: 1, limit: 1 })
        this.incomeSummary = res.data.summary || this.incomeSummary
        this.incomeSummaryReady = true
      } catch (error) {
        // 收入模块失败不阻塞作品管理，顶部保留加载状态供用户稍后重试。
        this.incomeSummaryReady = false
      }
    },
    refresh() {
      return this.initialize()
    },
    async loadActive(reset) {
      if (this.activeType === "fan_group") return
      if (this.listLoading) return
      this.listLoading = true
      try {
        const page = reset ? 1 : this.pagination.page + 1
        let res
        if (this.isWorkType) res = await getStudioWorks({ content_type: this.activeType, page, limit: 10 })
        else if (this.activeType === "tip") res = await getStudioTips({ page, limit: 10 })
        else if (this.activeType === "paid") res = await getStudioPaid({ page, limit: 10 })
        else res = await getStudioIncome({ page, limit: 10 })
        const rows = res.data.data || []
        this.items = reset ? rows : this.items.concat(rows)
        this.pagination = { page, hasMore: page * Number(res.data.limit) < Number(res.data.count) }
        if (this.activeType === "income") {
          this.incomeSummary = res.data.summary || this.incomeSummary
          this.incomeSummaryReady = true
        }
      } finally {
        this.listLoading = false
      }
    },
    switchType(type) {
      if (type === this.activeType) {
        this.scrollActiveTab(type)
        return
      }
      this.closePreview()
      this.closeManager()
      this.closeLiveGiftDetail()
      this.activeType = type
      this.items = []
      this.pagination = { page: 0, hasMore: false }
      if (type !== "fan_group") this.loadActive(true)
      this.scrollActiveTab(type)
    },
    handleTabLayoutChange() {
      this.$nextTick(() => {
        this.updateTabScrollState()
        this.scrollActiveTab(this.activeType, false)
      })
    },
    updateTabScrollState() {
      const container = this.$refs.studioTabs
      const buttons = container ? Array.from(container.querySelectorAll("[data-studio-tab]")) : []
      if (!container || !buttons.length) {
        this.tabScroll = { left: false, right: false }
        return
      }
      const containerRect = container.getBoundingClientRect()
      const firstRect = buttons[0].getBoundingClientRect()
      const lastRect = buttons[buttons.length - 1].getBoundingClientRect()
      this.tabScroll = {
        left: Math.min(firstRect.left, lastRect.left) < containerRect.left - 1,
        right: Math.max(firstRect.right, lastRect.right) > containerRect.right + 1
      }
    },
    scrollActiveTab(type, smooth = true) {
      this.$nextTick(() => {
        const container = this.$refs.studioTabs
        const button = container && Array.from(container.querySelectorAll("[data-studio-tab]"))
          .find(item => item.dataset.studioTab === type)
        if (!button) return
        button.scrollIntoView({ behavior: smooth ? "smooth" : "auto", block: "nearest", inline: "center" })
        window.setTimeout(this.updateTabScrollState, smooth ? 240 : 0)
      })
    },
    scrollTabs(direction) {
      const container = this.$refs.studioTabs
      const buttons = container ? Array.from(container.querySelectorAll("[data-studio-tab]")) : []
      if (!container || !buttons.length) return
      const containerRect = container.getBoundingClientRect()
      const clipped = buttons.map(button => ({ button, rect: button.getBoundingClientRect() })).filter(item => {
        return direction < 0 ? item.rect.left < containerRect.left - 1 : item.rect.right > containerRect.right + 1
      })
      clipped.sort((left, right) => direction < 0
        ? right.rect.right - left.rect.right
        : left.rect.left - right.rect.left)
      const target = clipped[0] && clipped[0].button
      if (!target) return
      target.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "center" })
      window.setTimeout(this.updateTabScrollState, 240)
    },
    selectCapability(type) {
      this.switchType(type)
      const allowed = type === "live" ? this.overview.roles.streamer_enabled : this.overview.roles.creator_enabled
      if (!allowed && type === "live") this.$emit("apply-role", "streamer")
      if (!allowed && type !== "live") this.$notice.info(this.$t("studioUi.creatorApplicationClosed"))
    },
    applyActiveRole() { this.$emit("apply-role", "streamer") },
    editable(item) { return [0, 3].includes(Number(item.review_status)) },
    isWorkOpenable(item) {
      return item.content_type !== "live"
        || (Number(item.live_status) === 1 && Number(item.status) === 1)
    },
    openWork(item) {
      if (!this.isWorkOpenable(item)) return
      if (item.content_type === "live") {
        // 真实开播中的直播复用用户端直播间；已结束记录仍只保留状态和礼物明细。
        this.$emit("open-live", Number(item.id))
        return
      }
      this.openPreview(item)
    },
    emptyForm() {
      if (this.activeType === "tip") return { id: 0, title: "", description: "", coin_price: 18, icon: "coffee", sort: 0 }
      if (this.activeType === "paid") return { id: 0, title: "", introduction: "", content_type: "album", cover_url: "", theme: "scene-rain", coin_price: 88, asset_urls_text: "" }
      return { id: 0, content_type: this.activeType, title: "", description: "", tags: "", video_url: "", low_video_url: "", cover_url: "", duration: 0, permission: "public", comments_enabled: 1, music_id: 0, music_name: "", theme: this.activeType === "live" ? "scene-live" : "scene-rain" }
    },
    async openCreateAction() {
      if (this.activeType === "live") {
        if (this.activeLiveItem) await this.toggleItem(this.activeLiveItem)
        else this.openLiveStart()
        return
      }
      this.openEditor()
    },
    openEditor(item) {
      if (this.activeType === "live") {
        if (!item) this.openLiveStart()
        return
      }
      const extra = item && this.activeType === "paid" ? { asset_urls_text: (item.asset_urls || []).join("\n") } : {}
      this.form = item ? { ...this.emptyForm(), ...item, ...extra, id: Number(item.id), coin_price: Number(item.coin_price || 0), duration: Number(item.duration || 0), music_id: Number(item.music_id || 0) } : this.emptyForm()
      this.videoUploadLabel = ""
      this.coverUploadLabel = ""
      this.videoUploadProgress = 0
      this.coverUploadProgress = 0
      this.paidAssetUploadProgress = 0
      this.paidAssetUploadLabel = ""
      if (this.activeType === "video") this.loadMusicOptions()
      this.closeManager()
      this.editorVisible = true
    },
    closeEditor() { if (!this.saving) this.editorVisible = false },
    openManager(item) {
      if (item.content_type === "live") {
        this.openLiveGiftDetail(item)
        return
      }
      this.managerItem = item
      this.managerVisible = true
    },
    closeManager() {
      this.managerVisible = false
      this.managerItem = null
    },
    openLiveGiftDetail(item) {
      this.liveGiftWork = item
      this.liveGiftVisible = true
      this.liveGiftRows = []
      this.liveGiftSummary = { coins: 0, orders: 0, quantity: 0 }
      this.liveGiftPagination = { page: 0, hasMore: false }
      this.loadLiveGiftDetail(true)
    },
    closeLiveGiftDetail() {
      this.liveGiftRequestId += 1
      this.liveGiftVisible = false
      this.liveGiftWork = null
      this.liveGiftRows = []
      this.liveGiftLoading = false
      this.liveGiftPagination = { page: 0, hasMore: false }
    },
    async loadLiveGiftDetail(reset) {
      if (this.liveGiftLoading || !this.liveGiftWork) return
      const requestId = ++this.liveGiftRequestId
      this.liveGiftLoading = true
      try {
        const page = reset ? 1 : this.liveGiftPagination.page + 1
        const liveId = Number(this.liveGiftWork.id)
        const res = await getStudioLiveGifts({ live_id: liveId, page, limit: 20 })
        if (requestId !== this.liveGiftRequestId || !this.liveGiftWork || Number(this.liveGiftWork.id) !== liveId) return
        const rows = res.data.data || []
        this.liveGiftRows = reset ? rows : this.liveGiftRows.concat(rows)
        this.liveGiftSummary = res.data.live_summary || this.liveGiftSummary
        this.liveGiftPagination = {
          page,
          hasMore: page * Number(res.data.limit) < Number(res.data.count)
        }
      } finally {
        if (requestId === this.liveGiftRequestId) this.liveGiftLoading = false
      }
    },
    async endLiveFromGiftDetail() {
      const item = this.liveGiftWork
      if (!item) return
      await this.toggleItem(item)
      if (this.liveGiftWork) this.liveGiftWork.live_status = 2
    },
    openManagerPreview() {
      const item = this.managerItem
      this.closeManager()
      if (item) this.openPreview(item)
    },
    openManagerComments() {
      const item = this.managerItem
      this.closeManager()
      if (!item) return
      this.openPreview(item)
      this.$nextTick(() => {
        const target = this.$refs.studioComments
        if (target && typeof target.scrollIntoView === "function") target.scrollIntoView({ behavior: "smooth", block: "start" })
      })
    },
    editManagedItem() {
      const item = this.managerItem
      if (item) this.openEditor(item)
    },
    async submitManagedItem() {
      const item = this.managerItem
      if (!item) return
      await this.submitItem(item)
      this.closeManager()
    },
    async toggleManagedItem() {
      const item = this.managerItem
      if (!item) return
      await this.toggleItem(item)
      this.closeManager()
    },
    async removeManagedItem() {
      const item = this.managerItem
      if (!item) return
      await this.removeItem(item)
      this.closeManager()
    },
    async updateManagedComments() {
      const item = this.managerItem
      if (!item) return
      const next = Number(item.comments_enabled) === 0 ? 1 : 0
      if (next === 0) {
        await this.$notice.confirm(this.$t("studioComments.disableConfirm"), this.$t("studioComments.disableTitle"), { type: "warning" })
      }
      await updateStudioWorkCommentStatus({ id: item.id, comments_enabled: next })
      this.$set(item, "comments_enabled", next)
      this.$notice.success(this.$t(next ? "studioComments.enabledSuccess" : "studioComments.disabledSuccess"))
    },
    normalizeAssetUrls(value) {
      if (Array.isArray(value)) return value.filter(Boolean)
      if (typeof value !== "string") return []
      try {
        const parsed = JSON.parse(value)
        if (Array.isArray(parsed)) return parsed.filter(Boolean)
      } catch (error) {
        // 兼容旧数据中以换行或英文逗号保存的资源地址。
      }
      return value.split(/[\r\n,]+/).map(item => item.trim()).filter(Boolean)
    },
    openPreview(item) {
      // 直播历史只提供状态和礼物明细，不允许重新播放已经结束的签名直播流。
      if (item.content_type === "live") return
      const sourceType = this.activeType === "paid" ? "paid" : "work"
      const assets = sourceType === "paid"
        ? this.normalizeAssetUrls(item.asset_urls)
        : [item.video_url]
      if (!assets.filter(Boolean).length) {
        this.$notice.warning(this.$t("studioPreview.unavailable"))
        return
      }
      this.previewSourceType = sourceType
      this.previewItem = item
      this.previewImageIndex = 0
      this.previewVideoError = false
      this.previewVisible = true
      if (sourceType === "work" && item.content_type === "video") this.loadPreviewComments()
    },
    closePreview() {
      const video = this.$refs.previewVideo
      if (video && typeof video.pause === "function") video.pause()
      this.previewVisible = false
      this.previewItem = null
      this.previewSourceType = ""
      this.previewImageIndex = 0
      this.previewVideoError = false
      this.previewComments = []
      this.previewCommentText = ""
      this.previewReplying = null
    },
    async loadPreviewComments() {
      if (!this.previewItem) return
      this.previewCommentsLoading = true
      try {
        const res = await getComments({ video_id: this.previewItem.id })
        this.previewComments = res.data.list || []
        this.$set(this.previewItem, "comments_enabled", res.data.comments_enabled ? 1 : 0)
      } finally {
        this.previewCommentsLoading = false
      }
    },
    startPreviewReply(comment) {
      this.previewReplying = comment
      this.previewCommentText = ""
    },
    cancelPreviewReply() {
      this.previewReplying = null
      this.previewCommentText = ""
    },
    async postPreviewComment() {
      if (!this.previewItem || !this.previewCommentsEnabled || !this.previewCommentText || this.previewCommentSaving) return
      this.previewCommentSaving = true
      try {
        const res = await addComment({
          video_id: this.previewItem.id,
          parent_id: this.previewReplying ? this.previewReplying.id : 0,
          content: this.previewCommentText
        })
        this.previewComments.unshift(res.data.comment)
        this.$set(this.previewItem, "comment_count", Number(res.data.comment_count || 0))
        this.previewCommentText = ""
        this.previewReplying = null
      } finally {
        this.previewCommentSaving = false
      }
    },
    async removePreviewComment(comment) {
      await this.$notice.confirm(
        this.$t("studioComments.removeConfirm", { name: comment.user_name }),
        this.$t("studioComments.remove"),
        { type: "warning" }
      )
      const res = await deleteComment({ comment_id: comment.id })
      const deletedIds = new Set((res.data.comment_ids || [res.data.comment_id]).map(Number))
      this.previewComments = this.previewComments.filter(item => !deletedIds.has(Number(item.id)))
      this.$set(this.previewItem, "comment_count", Number(res.data.comment_count || 0))
      this.$notice.success(this.$t("studioComments.removed"))
    },
    stepPreview(step) {
      const next = this.previewImageIndex + step
      if (next >= 0 && next < this.previewAssets.length) this.previewImageIndex = next
    },
    openCurrentPreviewImage() { openImageViewer(this.previewAssets, this.previewImageIndex, { title: this.previewItem ? this.previewItem.title : "" }) },
    retryPreviewVideo() {
      this.previewVideoError = false
      this.$nextTick(() => {
        const video = this.$refs.previewVideo
        if (!video) return
        video.load()
        const playTask = video.play()
        if (playTask && typeof playTask.catch === "function") playTask.catch(() => {})
      })
    },
    usePreset(preset) {
      this.form.video_url = preset.video
      this.form.low_video_url = preset.video
      this.form.cover_url = preset.cover
      if (!this.form.duration) this.form.duration = 10
    },
    async loadMusicOptions() {
      if (this.musicLoading || this.musicOptions.length) return
      this.musicLoading = true
      try {
        const res = await getMusicList({ category: "recommend", page: 1, limit: 30 })
        this.musicOptions = res.data.list || []
      } finally {
        this.musicLoading = false
      }
    },
    selectMusic(value) {
      const track = this.musicOptions.find(item => Number(item.id) === Number(value))
      this.form.music_name = track ? track.title : ""
      if (!value) this.form.music_id = 0
    },
    formatDuration(seconds) {
      const value = Math.max(0, Number(seconds) || 0)
      return `${Math.floor(value / 60)}:${String(Math.floor(value % 60)).padStart(2, "0")}`
    },
    videoDuration(file) {
      return new Promise(resolve => {
        const video = document.createElement("video")
        const url = URL.createObjectURL(file)
        const done = value => {
          URL.revokeObjectURL(url)
          resolve(value)
        }
        video.preload = "metadata"
        video.onloadedmetadata = () => done(Number.isFinite(video.duration) ? Math.round(video.duration) : 0)
        video.onerror = () => done(0)
        video.src = url
      })
    },
    async handleVideoFile(event) {
      const input = event.target
      const file = input.files && input.files[0]
      input.value = ""
      if (!file || this.videoUploading) return
      if (file.size > 120 * 1024 * 1024) {
        this.$notice.warning(this.$t("errors.UPLOAD.VIDEO_TOO_LARGE"))
        return
      }
      this.videoUploading = true
      this.videoUploadProgress = 0
      try {
        const durationTask = this.videoDuration(file)
        const res = await uploadMedia(file, "video", progress => { this.videoUploadProgress = progress })
        this.form.video_url = res.data.url
        this.form.low_video_url = res.data.url
        const duration = await durationTask
        if (duration) this.form.duration = duration
        this.videoUploadLabel = file.name
        this.$notice.success(this.$t("studioUi.videoUploaded"))
      } finally {
        this.videoUploading = false
      }
    },
    async handleCoverFile(event) {
      const input = event.target
      const file = input.files && input.files[0]
      input.value = ""
      if (!file || this.coverUploading) return
      if (file.size > 10 * 1024 * 1024) {
        this.$notice.warning(this.$t("errors.UPLOAD.IMAGE_TOO_LARGE"))
        return
      }
      this.coverUploading = true
      this.coverUploadProgress = 0
      try {
        const scene = this.activeType === "paid" ? "paid_cover" : "video_cover"
        const res = await uploadMedia(file, scene, progress => { this.coverUploadProgress = progress })
        this.form.cover_url = res.data.url
        this.coverUploadLabel = file.name
        this.$notice.success(this.$t("studioUi.coverUploaded"))
      } finally {
        this.coverUploading = false
      }
    },
    setPaidAssetUrls(urls) {
      this.form.asset_urls_text = this.normalizeAssetUrls(urls).join("\n")
    },
    removePaidAsset(index) {
      const urls = this.paidAssetUrls.slice()
      urls.splice(index, 1)
      this.setPaidAssetUrls(urls)
    },
    async handlePaidAssetFiles(event) {
      const input = event.target
      let files = Array.from(input.files || [])
      input.value = ""
      if (!files.length || this.paidAssetUploading) return

      const isVideo = this.form.content_type === "video"
      const existing = isVideo ? [] : this.paidAssetUrls.slice()
      if (!isVideo && existing.length >= 20) {
        this.$notice.warning(this.$t("studioBusiness.albumLimitReached"))
        return
      }
      files = isVideo ? files.slice(0, 1) : files.slice(0, 20 - existing.length)
      const expectedPrefix = isVideo ? "video/" : "image/"
      const maxSize = isVideo ? 120 * 1024 * 1024 : 10 * 1024 * 1024
      const invalidFile = files.find(file => !String(file.type).startsWith(expectedPrefix) || file.size <= 0 || file.size > maxSize)
      if (invalidFile) {
        this.$notice.warning(this.$t(isVideo
          ? (invalidFile.size > maxSize ? "errors.UPLOAD.VIDEO_TOO_LARGE" : "errors.UPLOAD.INVALID_VIDEO")
          : (invalidFile.size > maxSize ? "errors.UPLOAD.IMAGE_TOO_LARGE" : "errors.UPLOAD.INVALID_IMAGE")))
        return
      }

      this.paidAssetUploading = true
      this.paidAssetUploadProgress = 0
      let uploaded = 0
      const urls = existing
      try {
        for (let index = 0; index < files.length; index += 1) {
          const file = files[index]
          this.paidAssetUploadLabel = file.name
          const res = await uploadMedia(file, isVideo ? "paid_video" : "paid_image", progress => {
            this.paidAssetUploadProgress = Math.round(((index + progress / 100) / files.length) * 100)
          })
          if (isVideo) urls.splice(0, urls.length, res.data.url)
          else urls.push(res.data.url)
          uploaded += 1
          this.setPaidAssetUrls(urls)
          if (!isVideo && !this.form.cover_url) this.form.cover_url = res.data.url
        }
        this.$notice.success(this.$t("studioBusiness.assetsUploaded", { count: this.$n(uploaded) }))
      } finally {
        this.paidAssetUploading = false
      }
    },
    async saveDraft(submit) {
      if (!this.form.title || (this.activeType === "video" && !this.form.video_url) || (this.activeType === "paid" && !this.form.asset_urls_text.trim())) {
        this.$notice.warning(this.$t(this.isBusinessType ? "studioBusiness.requiredTip" : "studioUi.requiredTip"))
        return
      }
      this.saving = true
      try {
        let res
        if (this.isWorkType) res = await saveStudioWork(this.form)
        else if (this.activeType === "tip") res = await saveStudioTip(this.form)
        else res = await saveStudioPaid({ ...this.form, asset_urls: this.form.asset_urls_text.split(/[\r\n,]+/).map(item => item.trim()).filter(Boolean) })
        if (submit) await this.submitByType(res.data.id)
        this.$notice.success(this.$t(submit ? "studioUi.submitted" : "studioUi.draftSaved"))
        this.editorVisible = false
        await this.refresh()
      } finally {
        this.saving = false
      }
    },
    async submitByType(id) {
      if (this.isWorkType) await submitStudioWork({ id })
      else if (this.activeType === "tip") await submitStudioTip({ id })
      else await submitStudioPaid({ id })
      this.$emit("notification-created")
    },
    async submitItem(item) {
      await this.$notice.confirm(this.$t("studioUi.submitConfirm"), this.$t("studioUi.submitReview"))
      await this.submitByType(item.id)
      this.$notice.success(this.$t("studioUi.submitted"))
      await this.refresh()
    },
    async toggleItem(item) {
      if (this.activeType === "live") {
        if (Number(item.live_status) !== 1 || this.liveEnding) return
        await this.$notice.confirm(
          this.$t("studioManager.endLiveTip"),
          this.$t("studioUi.stopLive"),
          { type: "warning" }
        )
        this.liveEnding = true
        try {
          await updateStudioLiveStatus({ id: item.id, live_status: 2 })
          this.$notice.success(this.$t("studioUi.liveEnded"))
        } finally {
          this.liveEnding = false
        }
      } else {
        const status = Number(item.status) === 1 ? 2 : 1
        if (this.activeType === "video") await updateStudioWorkStatus({ id: item.id, status })
        else if (this.activeType === "tip") await updateStudioTipStatus({ id: item.id, status })
        else await updateStudioPaidStatus({ id: item.id, status })
        this.$notice.success(this.$t(status === 1 ? "studioUi.republished" : "studioUi.hidden"))
      }
      await this.refresh()
    },
    openLiveStart() {
      this.liveStartTikTokId = (this.overview && this.overview.last_live_tiktok_id) || ""
      this.liveStartVisible = true
    },
    closeLiveStart() {
      if (this.liveStarting) return
      this.liveStartVisible = false
      this.liveStartTikTokId = ""
    },
    async confirmLiveStart() {
      if (this.liveStarting) return
      const tiktokId = this.liveStartTikTokId.trim().replace(/^@/, "")
      if (!/^[A-Za-z0-9_.]{2,24}$/.test(tiktokId)) {
        this.$notice.warning(this.$t("errors.LIVE.INVALID_TIKTOK_ID"))
        return
      }
      this.liveStarting = true
      try {
        await startStudioLive({ tiktok_id: tiktokId })
        this.$set(this.overview, "last_live_tiktok_id", tiktokId)
        this.$notice.success(this.$t("studioUi.liveStarted"))
        this.liveStartVisible = false
        this.liveStartTikTokId = ""
        await this.refresh()
      } finally {
        this.liveStarting = false
      }
    },
    async removeItem(item) {
      await this.$notice.confirm(this.$t("studioUi.deleteConfirm"), this.$t("studioUi.delete"))
      if (this.isWorkType) await deleteStudioWork({ id: item.id })
      else if (this.activeType === "tip") await deleteStudioTip({ id: item.id })
      else await deleteStudioPaid({ id: item.id })
      this.$notice.success(this.$t("studioUi.deleted"))
      await this.refresh()
    },
    async refresh() {
      const res = await getStudioOverview()
      this.overview = res.data
      await Promise.all([
        this.loadActive(true),
        this.loadIncomeSummary()
      ])
    },
    reviewStateClass(item) {
      if (item.content_type === "live") {
        if (Number(item.live_status) === 1) return "review-state state-live"
        if (Number(item.live_status) === 2) return "review-state state-ended"
        return "review-state state-ready"
      }
      return `review-state state-${Number(item.review_status)}`
    },
    reviewText(item) {
      if (item.content_type === "live") return this.workStatusText(item)
      return this.$t(["studioUi.draft", "studioUi.pending", "studioUi.approved", "studioUi.rejected"][Number(item.review_status)] || "studioUi.draft")
    },
    workStatusText(work) {
      if (work.content_type === "live") return this.$t(Number(work.live_status) === 1 ? "studioUi.liveNow" : Number(work.live_status) === 2 ? "studioUi.liveEnded" : "studioUi.readyToLive")
      return this.$t(Number(work.status) === 1 ? "studioUi.published" : Number(work.status) === 2 ? "studioUi.hidden" : "studioUi.unpublished")
    },
    coinAmount(value) { return this.$n(Number(value || 0), { minimumFractionDigits: 2, maximumFractionDigits: 2 }) },
    incomeIcon(type) { return { gift: "el-icon-present", tip: "el-icon-coffee-cup", paid: "el-icon-unlock" }[type] || "el-icon-coin" }
  }
}
</script>

<style scoped>
.studio-page{position:absolute;z-index:38;inset:0 0 62px;overflow:hidden;color:#f8f5ef;background:radial-gradient(circle at 88% 0,rgba(255,61,89,.22),transparent 33%),#0d0e11}.studio-header{height:82px;display:flex;align-items:center;justify-content:space-between;padding:17px 18px 11px;border-bottom:1px solid rgba(255,255,255,.07)}.studio-header small,.studio-hero small,.studio-editor header small,.studio-preview header small,.source-presets>small{color:#ff5167;font-size:9px;letter-spacing:2px}.studio-header h1{margin:2px 0 0;font-size:27px}.studio-header button,.studio-editor header button,.studio-preview header button{width:36px;height:36px;border:1px solid #37383e;border-radius:50%;background:#191a1f;color:#fff}.studio-scroll{height:calc(100% - 82px);padding:16px 16px 38px;overflow-y:auto;overflow-x:hidden;scrollbar-width:none}.studio-scroll::-webkit-scrollbar,.editor-scroll::-webkit-scrollbar,.preview-scroll::-webkit-scrollbar,.album-toolbar>div::-webkit-scrollbar,.studio-tabs::-webkit-scrollbar{display:none}.studio-hero{display:flex;justify-content:space-between;gap:12px;padding:22px;border:1px solid #2b2c32;border-radius:20px;background:linear-gradient(145deg,#21151a,#17181c)}.studio-hero h2{margin:5px 0;font-size:23px;overflow-wrap:anywhere}.studio-hero p{max-width:260px;margin:0;color:#999ba2;font-size:11px;line-height:1.55}.studio-hero>span{align-self:center;flex:none;text-align:center;color:#8e9097;font-size:9px}.studio-hero>span strong{display:block;color:#fff;font-size:25px}.capability-grid{display:grid;grid-template-columns:1fr 1fr;gap:10px;margin:13px 0 16px}.capability-grid button{display:flex;align-items:center;gap:10px;min-width:0;padding:14px 12px;border:1px solid #2a2b31;border-radius:16px;background:#17181c;color:#ddd;text-align:start}.capability-grid button.enabled{border-color:rgba(255,71,96,.5);background:#21171b}.capability-grid i{font-size:22px;color:#ff5368}.capability-grid span{min-width:0}.capability-grid strong,.capability-grid small{display:block;overflow-wrap:anywhere}.capability-grid strong{font-size:12px}.capability-grid small{margin-top:4px;color:#888a91;font-size:9px}.studio-tabs{display:flex;margin:0 -16px;padding:0 16px;border-block:1px solid #28292e;overflow-x:auto;scrollbar-width:none}.studio-tabs button{display:flex;align-items:center;gap:6px;flex:none;padding:13px 12px;border:0;border-bottom:2px solid transparent;background:transparent;color:#85878e;white-space:nowrap}.studio-tabs button.active{border-bottom-color:#ff455e;color:#fff;font-weight:700}.studio-tabs i{font-size:14px}.studio-tabs span{font-size:11px}.create-work,.apply-role{width:100%;margin:14px 0;padding:13px;border:0;border-radius:14px;background:#ff4059;color:#fff;font-weight:700}.create-work i,.apply-role i{margin-inline-end:7px}.apply-role{background:#24252b;color:#ff7182}.work-card,.business-card{display:flex;gap:12px;padding:14px 0;border-bottom:1px solid #26272c}.work-card.previewable,.business-card.previewable{cursor:pointer}.work-card.previewable:focus-visible,.business-card.previewable:focus-visible{outline:2px solid #ff5167;outline-offset:4px}.work-cover,.business-visual{position:relative;width:82px;height:106px;display:grid;place-items:center;flex:none;overflow:hidden;border-radius:13px;background:#24252a;color:#676970;font-size:25px}.work-cover img,.business-visual img{width:100%;height:100%;object-fit:cover}.work-cover span{position:absolute;inset-inline-start:5px;top:5px;padding:3px 6px;border-radius:7px;background:#ff4058;color:#fff;font-size:8px}.preview-mark{position:absolute;inset-inline-end:7px;bottom:7px;width:28px;height:28px;display:grid;place-items:center;border:1px solid rgba(255,255,255,.45);border-radius:50%;background:rgba(8,8,10,.7);color:#fff;font-size:13px;backdrop-filter:blur(8px)}.preview-mark i{font-size:13px;color:#fff}.work-copy,.business-copy{min-width:0;flex:1}.work-copy>div,.business-copy>header{display:flex;align-items:center;justify-content:space-between;gap:8px}.work-copy>div small{color:#65676e;font-size:9px}.business-copy header em{color:#ffb26a;font-size:10px;font-style:normal;white-space:nowrap}.review-state{padding:3px 7px;border-radius:8px;background:#282a30;color:#aaa;font-size:9px}.state-1{background:#332912;color:#ffc25d}.state-2{background:#153126;color:#54dba3}.state-3{background:#391a21;color:#ff7282}.work-copy h3,.business-copy h3{margin:8px 0 5px;overflow:hidden;font-size:14px;text-overflow:ellipsis;white-space:nowrap}.work-copy p,.business-copy p{min-height:28px;margin:0;color:#85878e;font-size:10px;line-height:1.45;display:-webkit-box;-webkit-box-orient:vertical;-webkit-line-clamp:2;overflow:hidden}.work-copy footer,.business-copy footer{display:flex;flex-wrap:wrap;gap:6px;margin-top:9px}.work-copy footer button,.business-copy footer button{padding:5px 8px;border:1px solid #34363c;border-radius:8px;background:transparent;color:#bbb;font-size:9px}.work-copy footer .primary,.business-copy footer .primary{border-color:#ff4a61;color:#ff6d7f}.work-copy footer .danger,.business-copy footer .danger{color:#8f7479}.scene-live{background:linear-gradient(145deg,#411c2d,#18191e)}.scene-sea{background:linear-gradient(145deg,#173542,#18191e)}.scene-bread{background:linear-gradient(145deg,#46351e,#18191e)}.income-panel{padding-top:16px}.income-total{display:flex;align-items:center;justify-content:space-between;padding:22px;border:1px solid rgba(255,177,83,.24);border-radius:20px;background:radial-gradient(circle at 100% 0,rgba(255,177,83,.18),transparent 44%),linear-gradient(145deg,#23191a,#17181c)}.income-total small,.income-total strong{display:block}.income-total small{color:#aaa;font-size:10px}.income-total strong{display:inline-block;margin-top:5px;font-size:34px}.income-total em{margin-inline-start:6px;color:#ffb26a;font-size:10px;font-style:normal}.income-total>i{color:#ffb26a;font-size:36px}.income-breakdown{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:8px;margin:10px 0 16px}.income-breakdown span{min-width:0;padding:12px 10px;border:1px solid #292a30;border-radius:14px;background:#17181c}.income-breakdown small,.income-breakdown strong{display:block}.income-breakdown small{min-height:25px;color:#777981;font-size:9px;line-height:1.35;overflow-wrap:anywhere}.income-breakdown strong{margin-top:4px;font-size:15px}.income-list article{display:flex;align-items:center;gap:11px;padding:13px 2px;border-bottom:1px solid #26272c}.income-list>article>i{width:38px;height:38px;display:grid;place-items:center;flex:none;border-radius:12px;background:#241d20;color:#ff6376}.income-list span{min-width:0;flex:1}.income-list strong,.income-list small{display:block}.income-list strong{overflow:hidden;text-overflow:ellipsis;white-space:nowrap}.income-list small{margin-top:4px;color:#74767d;font-size:9px;line-height:1.4;overflow-wrap:anywhere}.income-list em{color:#59d7a2;font-size:13px;font-style:normal;white-space:nowrap}.load-more{width:100%;margin-top:16px;padding:11px;border:1px solid #303138;border-radius:12px;background:#191a1e;color:#aaa}.studio-empty,.studio-loading{min-height:220px;display:grid;place-content:center;justify-items:center;gap:9px;color:#777981;text-align:center}.studio-empty.compact{min-height:170px}.studio-empty i{font-size:38px}.studio-empty h3{margin:7px 0 0;color:#eee}.studio-empty p{max-width:280px;margin:0;font-size:11px;line-height:1.5}.studio-editor,.studio-preview{position:absolute;z-index:2;inset:0;background:#0d0e11}.studio-preview{z-index:4;background:radial-gradient(circle at 50% 28%,rgba(255,58,87,.11),transparent 40%),#090a0d}.studio-editor header,.studio-preview>header{height:68px;display:flex;align-items:center;gap:13px;padding:0 16px;border-bottom:1px solid #28292e}.studio-editor header h2,.studio-preview header h2{max-width:240px;margin:2px 0 0;overflow:hidden;font-size:19px;text-overflow:ellipsis;white-space:nowrap}.studio-editor header>span{width:36px;margin-inline-start:auto}.studio-preview>header>.review-state{margin-inline-start:auto;white-space:nowrap}.editor-scroll,.preview-scroll{height:calc(100% - 68px);padding:18px 16px 34px;overflow-y:auto;overflow-x:hidden;scrollbar-width:none}.preview-scroll{padding:0 0 34px}.video-preview,.preview-stage{position:relative;min-height:390px;display:grid;place-items:center;background:#000}.video-preview video,.preview-stage>img{width:100%;max-height:66vh;display:block;object-fit:contain;background:#000}.video-preview video{min-height:390px}.preview-arrow{position:absolute;top:50%;width:38px;height:38px;border:1px solid rgba(255,255,255,.28);border-radius:50%;transform:translateY(-50%);background:rgba(8,8,10,.68);color:#fff;backdrop-filter:blur(8px)}.preview-arrow.previous{inset-inline-start:12px}.preview-arrow.next{inset-inline-end:12px}.preview-arrow:disabled{opacity:.2}.album-toolbar{padding:12px 16px;border-bottom:1px solid #24252a}.album-toolbar>span{display:block;margin-bottom:9px;color:#9b9da4;font-size:10px;text-align:center}.album-toolbar>div{display:flex;gap:8px;overflow-x:auto;scrollbar-width:none}.album-toolbar button{width:52px;height:65px;padding:0;flex:none;overflow:hidden;border:2px solid transparent;border-radius:9px;background:#1c1d22}.album-toolbar button.active{border-color:#ff5067}.album-toolbar img{width:100%;height:100%;object-fit:cover}.preview-info{padding:18px 18px 4px}.preview-info>div{display:flex;align-items:center;justify-content:space-between;gap:10px}.preview-info>div span{padding:5px 9px;border-radius:9px;background:#271b20;color:#ff7788;font-size:10px}.preview-info>div em{color:#ffb26a;font-size:11px;font-style:normal}.preview-info p{margin:15px 0;color:#c5c6ca;font-size:12px;line-height:1.7;overflow-wrap:anywhere}.preview-info>small{display:flex;gap:7px;margin-top:14px;padding:11px;border-radius:11px;background:#17181d;color:#85878e;font-size:10px;line-height:1.5}.preview-info>small i{margin-top:2px;color:#ff6074}.preview-error{min-height:390px;display:grid;place-content:center;justify-items:center;padding:24px;text-align:center}.preview-error>i{color:#ff5b70;font-size:38px}.preview-error h3{margin:13px 0 5px}.preview-error p{max-width:270px;margin:0;color:#898b92;font-size:11px;line-height:1.6}.preview-error button{margin-top:16px;padding:9px 18px;border:0;border-radius:11px;background:#ff4059;color:#fff}.source-presets{display:flex;align-items:center;gap:7px;margin-bottom:16px;overflow-x:auto}.source-presets>small{margin-inline-end:auto;white-space:nowrap}.source-presets button{flex:none;padding:6px 9px;border:1px solid #34353b;border-radius:12px;background:#1b1c20;color:#aaa;font-size:9px}.form-pair{display:grid;grid-template-columns:1fr 1fr;gap:10px}.form-pair .el-select,.form-pair .el-input-number{width:100%}.editor-note{display:flex;gap:7px;padding:11px;border-radius:11px;background:#191a1e;color:#8e9097;font-size:10px;line-height:1.55}.editor-actions{display:grid;grid-template-columns:1fr 1.5fr;gap:10px;margin-top:16px}.editor-actions button{padding:13px;border:1px solid #34353b;border-radius:13px;background:#191a1e;color:#ccc}.editor-actions .primary{border-color:#ff4058;background:#ff4058;color:#fff;font-weight:700}.editor-actions button:disabled{opacity:.5}[dir="rtl"] .studio-header,[dir="rtl"] .studio-scroll,[dir="rtl"] .studio-editor,[dir="rtl"] .studio-preview{direction:rtl}
.media-upload-grid{display:grid;grid-template-columns:1fr 1fr;gap:9px;margin-bottom:16px}.media-upload-grid article{min-width:0}.media-upload-grid button{width:100%;min-height:92px;display:grid;grid-template-columns:32px minmax(0,1fr) auto;align-items:center;gap:8px;padding:11px;border:1px solid #303139;border-radius:15px;background:linear-gradient(145deg,#1d1e24,#15161a);color:#ddd;text-align:start}.media-upload-grid button>i:first-child{width:32px;height:32px;display:grid;place-items:center;border-radius:10px;background:#2a2025;color:#ff6276;font-size:15px}.media-upload-grid button>i:last-child{color:#62646c;font-size:10px}.media-upload-grid button span{min-width:0}.media-upload-grid button strong,.media-upload-grid button small{display:block}.media-upload-grid button strong{font-size:11px}.media-upload-grid button small{max-height:30px;margin-top:4px;overflow:hidden;color:#777a82;font-size:8px;line-height:1.45;overflow-wrap:anywhere}.media-upload-grid button em{color:#ff8b9a;font-size:9px;font-style:normal}.media-upload-grid button:disabled{opacity:.68}.selected-music{display:grid;grid-template-columns:42px minmax(0,1fr) auto;align-items:center;gap:10px;margin-top:8px;padding:9px;border:1px solid rgba(32,226,215,.16);border-radius:13px;background:rgba(32,226,215,.055)}.selected-music img{width:42px;height:42px;border-radius:10px;object-fit:cover;background:#25262b}.selected-music span{min-width:0}.selected-music strong,.selected-music small{display:block;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}.selected-music strong{font-size:11px}.selected-music small{margin-top:4px;color:#81848b;font-size:8px}.selected-music>i{color:#20e2d7;font-size:17px}
.paid-upload-panel{margin:0 0 18px;padding:14px;border:1px solid rgba(255,64,89,.16);border-radius:18px;background:radial-gradient(circle at 100% 0,rgba(255,64,89,.1),transparent 35%),#15161a}.paid-upload-panel>header{display:flex;align-items:flex-end;justify-content:space-between;gap:12px;margin-bottom:12px}.paid-upload-panel>header small,.paid-upload-panel>header strong{display:block}.paid-upload-panel>header small{color:#ff6075;font-size:8px;letter-spacing:1.4px}.paid-upload-panel>header strong{margin-top:4px;font-size:13px}.paid-upload-panel>header em{max-width:142px;color:#777a82;font-size:8px;font-style:normal;line-height:1.5;text-align:end}.paid-media-upload{margin-bottom:0}.paid-media-upload button{min-height:84px}.paid-asset-list{display:grid;gap:7px;margin-top:11px}.paid-asset-list>article{display:grid;grid-template-columns:42px minmax(0,1fr) 30px;align-items:center;gap:9px;padding:8px;border:1px solid #292b31;border-radius:13px;background:#191a1f}.paid-asset-list>article>img,.paid-asset-list>article>span{width:42px;height:42px;display:grid;place-items:center;border-radius:9px;background:#25262c;object-fit:cover;color:#ff687b}.paid-asset-list>article>div{min-width:0}.paid-asset-list strong,.paid-asset-list small{display:block}.paid-asset-list strong{font-size:10px}.paid-asset-list small{margin-top:4px;overflow:hidden;color:#686b72;font-size:8px;text-overflow:ellipsis;white-space:nowrap}.paid-asset-list>article>button{width:28px;height:28px;border:1px solid #34353c;border-radius:9px;background:#222329;color:#8c8e95}.paid-asset-list>article>button:active{border-color:#ff6075;color:#ff7183}[dir="rtl"] .paid-upload-panel{direction:rtl}
.manage-entry{display:inline-flex;align-items:center;gap:5px}.studio-manager{position:absolute;z-index:3;inset:0;overflow:hidden;background:radial-gradient(circle at 90% 0,rgba(255,64,89,.16),transparent 34%),#0d0e11}.studio-manager>header{height:68px;display:flex;align-items:center;gap:13px;padding:0 16px;border-bottom:1px solid #28292e}.studio-manager>header button{width:36px;height:36px;border:1px solid #37383e;border-radius:50%;background:#191a1f;color:#fff}.studio-manager>header small{color:#ff5167;font-size:9px;letter-spacing:2px}.studio-manager>header h2{margin:2px 0 0;font-size:19px}.studio-manager>header>span{width:36px;margin-inline-start:auto}.manager-scroll{height:calc(100% - 68px);padding:18px 16px 38px;overflow-y:auto;overflow-x:hidden;scrollbar-width:none}.manager-scroll::-webkit-scrollbar{display:none}.manager-work{display:flex;align-items:center;gap:13px;padding:15px;border:1px solid #2a2c32;border-radius:18px;background:linear-gradient(145deg,#21171b,#17181d)}.manager-work>div{width:62px;height:78px;display:grid;place-items:center;flex:none;overflow:hidden;border-radius:12px;background:#24252b;color:#777a82;font-size:22px}.manager-work>div img{width:100%;height:100%;object-fit:cover}.manager-work>section{min-width:0}.manager-work h3{margin:8px 0 5px;overflow:hidden;font-size:15px;text-overflow:ellipsis;white-space:nowrap}.manager-work p{margin:0;color:#777a82;font-size:9px;overflow-wrap:anywhere}.manager-actions{margin-top:16px;overflow:hidden;border:1px solid #292b31;border-radius:18px;background:#17181c}.manager-actions>button{width:100%;display:grid;grid-template-columns:36px minmax(0,1fr) auto;align-items:center;gap:11px;min-width:0;padding:13px 14px;border:0;border-bottom:1px solid #27292f;background:transparent;color:#d8d9dd;text-align:start}.manager-actions>button:last-child{border-bottom:0}.manager-actions>button:hover{background:#1d1e23}.manager-actions>button>i:first-child{width:36px;height:36px;display:grid;place-items:center;border-radius:11px;background:#242128;color:#ff6175;font-size:16px}.manager-actions>button>i:last-child{color:#555861;font-size:12px}.manager-actions>button>span{min-width:0}.manager-actions strong,.manager-actions small{display:block;overflow-wrap:anywhere}.manager-actions strong{font-size:12px}.manager-actions small{margin-top:4px;color:#777a82;font-size:9px;line-height:1.45}.manager-actions em{max-width:108px;padding:5px 7px;border-radius:9px;background:#282a30;color:#9699a1;font-size:8px;font-style:normal;line-height:1.35;text-align:center;overflow-wrap:anywhere}.manager-actions em.enabled{background:#173328;color:#61dbaa}.manager-actions>button.danger>i:first-child{background:#291b20;color:#ff7182}.manager-actions>button.danger strong{color:#ff8796}[dir="rtl"] .studio-manager{direction:rtl}[dir="rtl"] .manager-actions>button>i:last-child{transform:rotate(180deg)}
.studio-comments{margin:10px 12px 0;padding:16px;border:1px solid #282a31;border-radius:18px;background:linear-gradient(150deg,#17181d,#111216)}.studio-comments>header{display:flex;align-items:center;justify-content:space-between;gap:12px}.studio-comments>header small{color:#ff6175;font-size:8px;letter-spacing:1.5px}.studio-comments>header h3{margin:4px 0 0;font-size:15px}.studio-comment-list{margin-top:12px}.studio-comment-list>article{display:grid;grid-template-columns:32px minmax(0,1fr);gap:9px;padding:11px 0;border-top:1px solid #24262c}.studio-comment-list>article.reply{margin-inline-start:25px}.studio-comment-list>article>.studio-comment-avatar{position:relative;width:32px;height:32px;display:block}.studio-comment-avatar>img:first-child{width:32px;height:32px;border-radius:50%;object-fit:cover;background:linear-gradient(140deg,#2e3038,#ff4059)}.studio-comment-avatar>.studio-comment-avatar-frame{position:absolute;inset:-7px;width:46px;height:46px;object-fit:contain;pointer-events:none}.studio-comment-list>article>div{min-width:0}.studio-comment-list strong{display:block;color:#a9abb2;font-size:10px;overflow-wrap:anywhere}.studio-comment-list strong em{color:#71747d;font-style:normal;font-weight:400}.studio-comment-list p{margin:5px 0 7px;color:#ececef;font-size:12px;line-height:1.55;overflow-wrap:anywhere}.studio-comment-list footer{display:flex;align-items:center;flex-wrap:wrap;gap:10px}.studio-comment-list footer small{margin-inline-end:auto;color:#686b73;font-size:8px}.studio-comment-list footer button{padding:0;border:0;background:transparent;color:#9a9ca4;font-size:9px}.studio-comment-list footer button.danger{color:#ff7182}.studio-comment-state{min-height:90px;display:flex;align-items:center;justify-content:center;gap:8px;color:#747780;font-size:10px}.studio-comment-editor{margin-top:12px;padding-top:12px;border-top:1px solid #292b31}.studio-comment-editor>div:last-child{display:grid;grid-template-columns:minmax(0,1fr) 48px;gap:8px}.studio-comment-editor>div:last-child>button{border:0;border-radius:12px;background:#ff4059;color:#fff;font-size:10px;font-weight:700}.studio-comment-editor>div:last-child>button:disabled{opacity:.45}.studio-replying{display:flex!important;align-items:center;justify-content:space-between;margin-bottom:7px;color:#8c8f97;font-size:9px}.studio-replying button{padding:0;border:0;background:transparent;color:#ff7182}.studio-comments-disabled{display:flex;align-items:center;justify-content:center;gap:7px;margin:14px 0 0;padding:10px;border-radius:11px;background:#1c1d22;color:#7f828a;font-size:10px;line-height:1.45;text-align:center}
.review-state.state-live{background:#3b1820;color:#ff7588}.review-state.state-ended{background:#282a30;color:#9699a2}.review-state.state-ready{background:#332912;color:#ffc25d}.create-work.stop-live{border:1px solid rgba(255,112,128,.55);background:linear-gradient(135deg,#55212c,#cf3048);box-shadow:0 9px 24px rgba(207,48,72,.18)}.work-copy footer .live-stop{border-color:rgba(255,82,105,.5);background:rgba(255,64,89,.1);color:#ff8392}.work-copy footer button:disabled{opacity:.5;cursor:not-allowed}
.live-gift-work{position:relative;padding-inline-end:108px}.gift-stop-live{position:absolute;inset-inline-end:13px;top:50%;display:inline-flex;align-items:center;gap:5px;padding:8px 10px;transform:translateY(-50%);border:1px solid rgba(255,82,105,.46);border-radius:10px;background:rgba(255,64,89,.1);color:#ff8392;font-size:9px}.gift-stop-live:disabled{opacity:.5}.live-gift-summary{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:8px;margin:14px 0}.live-gift-summary>span{min-width:0;padding:13px 8px;border:1px solid #2b2c32;border-radius:14px;background:linear-gradient(145deg,#1d1e23,#15161a);text-align:center}.live-gift-summary small,.live-gift-summary strong{display:block}.live-gift-summary small{min-height:26px;color:#85878f;font-size:8px;line-height:1.5;overflow-wrap:anywhere}.live-gift-summary strong{margin-top:5px;color:#fff;font-size:19px}.live-gift-list{overflow:hidden;border:1px solid #292b31;border-radius:18px;background:#17181c}.live-gift-list>article{display:grid;grid-template-columns:42px minmax(0,1fr) auto;align-items:center;gap:11px;padding:13px 14px;border-bottom:1px solid #27292f}.live-gift-list>article:last-child{border-bottom:0}.live-gift-list img{width:42px;height:42px;border-radius:50%;object-fit:cover;background:#272830}.live-gift-list span{min-width:0}.live-gift-list strong,.live-gift-list small,.live-gift-list time{display:block;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}.live-gift-list strong{font-size:11px}.live-gift-list small{margin-top:3px;color:#ff9aa8;font-size:9px}.live-gift-list time{margin-top:3px;color:#676a73;font-size:8px}.live-gift-list em{color:#ffc36a;font-size:13px;font-style:normal;font-weight:700}.live-gift-empty{margin-top:14px}.live-gift-loading{min-height:160px}.live-gift-detail .load-more{margin-top:14px}[dir="rtl"] .live-gift-detail{direction:rtl}
@media(prefers-reduced-motion:no-preference){.preview-mark{transition:transform .2s ease,background .2s ease}.previewable:active .preview-mark{transform:scale(.88);background:#ff4059}}
.withdrawable-income{position:relative;width:100%;display:grid;grid-template-columns:42px minmax(0,1fr) auto;align-items:center;gap:12px;margin:11px 0 0;padding:13px 14px;overflow:hidden;border:1px solid rgba(255,177,83,.28);border-radius:16px;background:linear-gradient(110deg,rgba(255,177,83,.13),rgba(255,64,89,.08)),#17181c;color:#fff;text-align:start}.withdrawable-income::after{content:"";position:absolute;inset-inline-end:-22px;top:-35px;width:90px;height:90px;border-radius:50%;background:rgba(255,177,83,.1);filter:blur(4px);pointer-events:none}.withdrawable-income>i{width:42px;height:42px;display:grid;place-items:center;border-radius:13px;background:linear-gradient(145deg,#ffb45f,#ff596e);color:#151517;font-size:21px;box-shadow:0 8px 20px rgba(255,100,91,.18)}.withdrawable-income>span{min-width:0}.withdrawable-income small,.withdrawable-income strong{display:block}.withdrawable-income small{color:#a9abb1;font-size:9px;letter-spacing:.4px}.withdrawable-income strong{margin-top:3px;font-size:21px;line-height:1.15}.withdrawable-income strong em{color:#ffbd73;font-size:9px;font-style:normal;font-weight:500}.withdrawable-income strong>i{color:#ffb26a;font-size:16px}.withdrawable-income>b{position:relative;z-index:1;display:flex;align-items:center;gap:4px;max-width:112px;color:#d1a36e;font-size:9px;font-weight:600;line-height:1.35;text-align:end;overflow-wrap:anywhere}.withdrawable-income>b i{flex:none;font-size:10px}[dir="rtl"] .withdrawable-income>b i{transform:rotate(180deg)}
.studio-live-start{position:fixed;z-index:1300;inset:0;display:grid;place-items:center;box-sizing:border-box;padding:calc(16px + env(safe-area-inset-top)) 16px calc(16px + env(safe-area-inset-bottom));overflow-y:auto;overscroll-behavior:contain;color:#f8f5ef}.live-start-backdrop{position:fixed;inset:0;border:0;background:rgba(3,4,7,.78);backdrop-filter:blur(8px)}.live-start-card{position:relative;width:min(100%,398px);max-height:calc(100vh - 32px);max-height:calc(100dvh - 32px - env(safe-area-inset-top) - env(safe-area-inset-bottom));box-sizing:border-box;padding:18px;overflow-y:auto;overscroll-behavior:contain;border:1px solid rgba(255,255,255,.1);border-radius:22px;background:radial-gradient(circle at 100% 0,rgba(255,64,89,.2),transparent 38%),linear-gradient(155deg,#202126,#111216);box-shadow:0 24px 60px rgba(0,0,0,.48)}.live-start-card>header{display:grid;grid-template-columns:42px minmax(0,1fr) 34px;align-items:center;gap:11px}.live-start-card>header>span{width:42px;height:42px;display:grid;place-items:center;border-radius:14px;background:linear-gradient(145deg,#ff526a,#ff3158);font-size:19px}.live-start-card>header small{color:#ff7183;font-size:8px;letter-spacing:1.8px}.live-start-card>header h2{margin:3px 0 0;font-size:20px;overflow-wrap:anywhere}.live-start-card>header>button{width:34px;height:34px;border:1px solid #34363d;border-radius:50%;background:#1c1d22;color:#aaa}.live-start-card>p{margin:16px 0;color:#a0a2a9;font-size:11px;line-height:1.65;overflow-wrap:anywhere}.live-start-note{display:flex;gap:8px;margin-top:5px;padding:11px;border-radius:12px;background:rgba(32,226,215,.06);color:#8f979b;font-size:9px;line-height:1.55}.live-start-note i{margin-top:2px;color:#20e2d7}.live-start-actions{display:grid;grid-template-columns:1fr 1.55fr;gap:9px;margin-top:16px}.live-start-actions button{min-height:43px;border:1px solid #34363c;border-radius:13px;background:#1b1c21;color:#c7c8cc}.live-start-actions .primary{border-color:#ff4059;background:#ff4059;color:#fff;font-weight:700}.live-start-actions button:disabled,.create-work:disabled{opacity:.48;cursor:not-allowed}.live-start-actions i{margin-inline-end:5px}[dir="rtl"] .studio-live-start{direction:rtl}
.studio-tabs-shell{position:relative;width:calc(100% + 32px);margin:0 -16px;overflow:hidden;border-block:1px solid #28292e;background:rgba(13,14,17,.84)}
.studio-tabs-shell::before,.studio-tabs-shell::after{content:"";position:absolute;z-index:1;top:0;bottom:0;width:54px;opacity:0;pointer-events:none;transition:opacity .18s ease}.studio-tabs-shell::before{left:0;background:linear-gradient(90deg,#0d0e11 14%,rgba(13,14,17,0))}.studio-tabs-shell::after{right:0;background:linear-gradient(270deg,#0d0e11 14%,rgba(13,14,17,0))}.studio-tabs-shell.can-scroll-left::before,.studio-tabs-shell.can-scroll-right::after{opacity:1}
.studio-tabs{width:100%;max-width:100%;box-sizing:border-box;margin:0;padding:0 16px;border:0;overscroll-behavior-inline:contain;scroll-behavior:smooth;scroll-padding-inline:52px;scroll-snap-type:x proximity;-webkit-overflow-scrolling:touch}.studio-tabs button{min-height:46px;scroll-snap-align:center}.studio-tabs button.active{position:relative;text-shadow:0 0 18px rgba(255,69,94,.32)}
.studio-tab-scroll{position:absolute;z-index:2;top:50%;width:30px;height:30px;display:grid;place-items:center;padding:0;transform:translateY(-50%);border:1px solid rgba(255,255,255,.13);border-radius:50%;background:rgba(29,30,35,.94);color:#f5f5f7;box-shadow:0 7px 18px rgba(0,0,0,.3);backdrop-filter:blur(10px)}.studio-tab-scroll.previous{left:7px}.studio-tab-scroll.next{right:7px}.studio-tab-scroll:active{border-color:rgba(255,79,103,.55);background:#ff4059;transform:translateY(-50%) scale(.92)}
@media(prefers-reduced-motion:reduce){.studio-tabs{scroll-behavior:auto}.studio-tabs-shell::before,.studio-tabs-shell::after{transition:none}}
@media(min-width:900px){.studio-page{left:50%;width:430px;transform:translateX(-50%)}}
.studio-page.embedded{position:relative;z-index:1;inset:auto;width:100%;max-width:100%;min-width:0;min-height:720px;box-sizing:border-box;overflow:visible;transform:none;background:transparent}.studio-page.embedded>.studio-scroll{width:100%;min-width:0;height:auto;box-sizing:border-box;padding:16px 15px 38px;overflow:visible}.studio-page.embedded .studio-tabs-shell{width:100%;max-width:100%;margin-inline:0}.studio-page.embedded .studio-tabs{margin-inline:0;padding-inline:12px}.studio-page.embedded .studio-editor,.studio-page.embedded .studio-preview{position:absolute;width:100%;max-width:100%;min-height:720px;box-sizing:border-box;border-radius:18px}.studio-page.embedded .editor-scroll,.studio-page.embedded .preview-scroll{height:auto;max-height:none;overflow:visible}
.studio-page:not(.embedded){bottom:var(--bottom-nav-height)}
</style>
